import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react'
import { MapContainer, TileLayer, Marker as LeafletMarker, useMap, Polyline, Tooltip as LTooltip, ZoomControl, GeoJSON } from 'react-leaflet'
import { basemapUrl, BASEMAP_ATTRIB } from '../lib/basemap'
import L, { DivIcon } from 'leaflet'
import 'leaflet/dist/leaflet.css'
import 'leaflet.heat'
import { useAuth } from '../contexts/AuthContext'
import 'leaflet.markercluster'
import 'leaflet.markercluster/dist/MarkerCluster.css'
import 'leaflet.markercluster/dist/MarkerCluster.Default.css'
import { Tooltip } from 'react-tooltip'
import { FiMapPin, FiMinimize2, FiUpload, FiUsers, FiMove, FiRefreshCw, FiTrash2, FiCheck, FiAlertCircle, FiAlertTriangle, FiX, FiDownload, FiMonitor, FiSearch, FiEdit2, FiPhone, FiDatabase, FiNavigation, FiFilter, FiSettings, FiChevronDown, FiChevronRight, FiUser, FiMail, FiCrosshair } from 'react-icons/fi'
import { API_ENDPOINTS } from '../lib/api'
import { ExecutivoPanel } from './ExecutivoPanel'
// AtivasColumnMappingModal removido — sync Oracle substituiu upload manual
// GeocodingUsageDashboard removido — stats inline no header via fetch direto

// Normaliza nome de cidade pra casar com o GeoJSON do IBGE (mesma função do
// painel Rede, LeaderCoverageView.tsx) — usada pela camada de cobertura por
// município (verde=com líder / cinza=sem líder).
const normCidade = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toUpperCase().trim()

// Hook: detecta tema escuro (classe .dark no <html>) reativamente — usado pra
// alternar o tile base (CARTO Voyager claro ↔ Dark Matter escuro).
const useIsDark = () => {
  const [isDark, setIsDark] = useState(() =>
    typeof document !== 'undefined' && document.documentElement.classList.contains('dark')
  )
  useEffect(() => {
    const el = document.documentElement
    const obs = new MutationObserver(() => setIsDark(el.classList.contains('dark')))
    obs.observe(el, { attributes: true, attributeFilter: ['class'] })
    return () => obs.disconnect()
  }, [])
  return isDark
}

// Confiança da geocodificação → estilo do pin (bloco "camada de confiança").
// high/manual = sólido; medium = leve transparência; low/none = âmbar de alerta.
type GeoConfidence = 'high' | 'medium' | 'low' | 'none' | 'manual' | null | undefined
const confidenceMeta = (c: GeoConfidence, source?: string | null) => {
  const conf = (c || '').toLowerCase()
  if (conf === 'manual') return { level: 'manual', opacity: 1, alert: false, label: 'ajustada manualmente' }
  if (conf === 'high')   return { level: 'high', opacity: 1, alert: false, label: source === 'brasilapi' ? 'exata (CEP)' : 'exata' }
  if (conf === 'medium') {
    const rua = source === 'nominatim_rua' || source === 'cnefe_rua' || source === 'cnefe_cep_rua'
    return { level: 'medium', opacity: 0.85, alert: false, label: rua ? 'aproximada (rua)' : 'aproximada (endereço)' }
  }
  // 'low' não é uma coisa só: bairro/RA é uma vizinhança de verdade (Ceilândia,
  // Samambaia), enquanto o centróide é o meio do município. Rotular os dois como
  // "centro da cidade" é mentira na frente de quem conhece a praça — e só o
  // nível de cidade merece o alerta âmbar.
  if (conf === 'low' || conf === 'none') {
    const bairro = source === 'nominatim_bairro' || source === 'cnefe_bairro' || source === 'cnefe_cep_bairro'
    if (bairro) return { level: 'low', opacity: 0.9, alert: false, label: 'aproximada (bairro)' }
    return { level: 'low', opacity: 0.9, alert: true, label: 'imprecisa (centro da cidade)' }
  }
  // Confiança desconhecida (não informada) — sem alerta, estilo normal.
  return { level: 'unknown', opacity: 1, alert: false, label: 'não informada' }
}

// Ícone de pin — círculo chapado, exatamente igual ao dotIcon/leadIcon do
// painel Rede (LeaderCoverageView.tsx): mesmo tamanho pra todo mundo (sem
// distinção de hierarquia C1/C2/C3), sem foto, sem anel de confiança na cor
// do pin. A confiança da geocodificação continua visível no popup ao clicar
// (AtivaPopupContent) — só deixou de ser um indicador permanente no mapa.
//
// EXCECAO reintroduzida em 02/09/2026: `alerta`. Nao e a volta do anel de
// confianca de 4 niveis que saiu daqui — e um sinal BINARIO, so para o caso
// que mente: coordenada de centroide de municipio. Esses pontos sao
// FABRICADOS (centro da sede + ruido aleatorio de +-3,3 km) e, sem marca
// nenhuma, ficam visualmente identicos a um endereco exato. Os outros niveis
// (bairro, rua, aproximada) continuam sem indicador permanente, como decidido.
const createColoredIcon = (color: string, alerta = false) => {
  // class "animated-marker" → pop-in com bounce (keyframe marker-pop-in) quando
  // o dot ENTRA no DOM. Como cada AtivaMarker monta ao aparecer (mudança de
  // semana/carga), só os dots NOVOS pulam — restaura o efeito "pipoca conforme
  // vão ativando" (2026-07-14). Os que permanecem não re-animam.
  return new DivIcon({
    html: `<div class="animated-marker" style="filter:drop-shadow(0 1px 2px rgba(0,0,0,0.4))">
      <svg width="16" height="16" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <circle cx="12" cy="12" r="9" fill="${color}" stroke="#fff" stroke-width="2"
          ${alerta ? 'stroke-dasharray="3 2.5" stroke-opacity="0.95"' : ''}/>
        ${alerta ? '<circle cx="12" cy="12" r="3" fill="#fff" fill-opacity="0.85"/>' : ''}
      </svg>
    </div>`,
    iconSize: [16, 16],
    iconAnchor: [8, 8],
    popupAnchor: [0, -8],
    className: 'leaflet-colored-marker'
  })
}

// Cache de ícones coloridos por cor (mesmo pin, mesmo tamanho, pra todo mundo).
const coloredIconCache = new Map<string, DivIcon>()
const getColoredIcon = (color: string, alerta = false): DivIcon => {
  const chave = `${color}|${alerta ? 'aprox' : 'ok'}`
  if (!coloredIconCache.has(chave)) {
    coloredIconCache.set(chave, createColoredIcon(color, alerta))
  }
  return coloredIconCache.get(chave)!
}

// Pin AMPLIADO pro grupo em foco no isolamento. Motivo: o flyToBounds enquadra
// a equipe inteira — uma consultora a 185km (caso real: grupo REALIZAR, Rio
// Verde ↔ Costa Rica) força zoom estadual e as linhas de ~1km viram sub-pixel,
// parecendo "só uma linkagem". Pin maior + linha grossa mantêm o grupo legível
// mesmo aglomerado nesse zoom.
const createFocusIcon = (color: string) => new DivIcon({
  html: `<div style="filter:drop-shadow(0 1px 3px rgba(0,0,0,0.55))">
    <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="10" fill="${color}" stroke="#fff" stroke-width="2.5"/>
    </svg>
  </div>`,
  iconSize: [24, 24],
  iconAnchor: [12, 12],
  popupAnchor: [0, -12],
  className: 'leaflet-colored-marker'
})
const focusIconCache = new Map<string, DivIcon>()
const getFocusIcon = (color: string): DivIcon => {
  if (!focusIconCache.has(color)) {
    focusIconCache.set(color, createFocusIcon(color))
  }
  return focusIconCache.get(color)!
}

// Componente HeatmapLayer removido - agora usando CircleMarker para efeito de calor
// O CircleMarker funciona melhor com toggle ativar/desativar no React-Leaflet

// HEATMAP DE VERDADE (leaflet.heat).
//
// O que existia aqui antes eram CircleMarker de raio FIXO EM PIXELS (18/25/30)
// com fillOpacity fixa, um por pessoa. Isso não é mapa de calor: não há kernel
// de densidade, não há escala, e o raio em pixels não acompanha o terreno — em
// zoom baixo tudo vira uma mancha uniforme e em zoom alto o "calor" some.
// Também mentia na leitura: duas consultoras no mesmo quarteirão pintavam
// menos que uma sozinha com o círculo maior de empresária.
//
// Agora é densidade real: peso 1 por pessoa, raio em pixels reagindo ao zoom
// (mais zoom, raio maior, para o gradiente não virar poeira) e gradiente com
// escala declarada na legenda.
const HEAT_GRADIENT = { 0.2: '#3b82f6', 0.4: '#22c55e', 0.6: '#facc15', 0.8: '#f97316', 1.0: '#ef4444' }

function HeatLayer({ points, show }: { points: [number, number][]; show: boolean }) {
  const map = useMap()
  const layerRef = useRef<any>(null)

  useEffect(() => {
    if (!show) {
      if (layerRef.current) { map.removeLayer(layerRef.current); layerRef.current = null }
      return
    }
    const L2 = L as any
    if (typeof L2.heatLayer !== 'function') return  // plugin ausente: degrada sem quebrar
    if (!layerRef.current) {
      layerRef.current = L2.heatLayer(points, {
        radius: 22, blur: 18, maxZoom: 14, minOpacity: 0.25, gradient: HEAT_GRADIENT,
      })
      layerRef.current.addTo(map)
    } else {
      layerRef.current.setLatLngs(points)
    }
    return () => {
      if (layerRef.current) { map.removeLayer(layerRef.current); layerRef.current = null }
    }
  }, [show, points, map])

  // O raio em pixels precisa crescer com o zoom, senão o gradiente vira poeira
  // de pontos isolados quando se aproxima.
  useEffect(() => {
    if (!show) return
    const ajustar = () => {
      if (!layerRef.current) return
      const z = map.getZoom()
      const r = z <= 6 ? 12 : z <= 9 ? 18 : z <= 12 ? 26 : 36
      layerRef.current.setOptions({ radius: r, blur: Math.round(r * 0.8) })
    }
    ajustar()
    map.on('zoomend', ajustar)
    return () => { map.off('zoomend', ajustar) }
  }, [show, map])

  return null
}

// Componente para ajustar zoom/bounds automaticamente
// Ajusta sempre que a quantidade de markers mudar significativamente
const MapBoundsAdjuster = ({ markers, resetKey, enabled = true }: { markers: Array<{ lat: number; lng: number }>; resetKey?: string; enabled?: boolean }) => {
  const map = useMap()
  const lastCountRef = useRef(0)
  const lastResetRef = useRef<string | undefined>(undefined)

  useEffect(() => {
    // `enabled` segura o primeiro enquadramento até as preferências chegarem:
    // quem tem Estado salvo abria na praça inteira e só depois pulava pro
    // recorte — dois fitBounds seguidos, visíveis como tranco.
    if (!enabled) return

    // Troca de recorte (Estado) força o enquadramento, independente do limiar:
    // dois Estados de tamanho parecido não mudariam a contagem o bastante e a
    // câmera ficaria parada sobre a região anterior. Precisa ser AQUI, no mesmo
    // efeito que enquadra — num efeito separado o reset da ref não agenda nada,
    // só envenena o limiar pro próximo render (bug de 18/08).
    const recorteMudou = lastResetRef.current !== resetKey
    lastResetRef.current = resetKey

    // Ajustar se:
    // 1. É a primeira vez (lastCount = 0)
    // 2. A quantidade de markers mudou significativamente (>10% ou >20 markers)
    const countDiff = Math.abs(markers.length - lastCountRef.current)
    const shouldAdjust = recorteMudou || lastCountRef.current === 0 || countDiff > 20 || countDiff / Math.max(lastCountRef.current, 1) > 0.1

    if (markers.length > 0 && shouldAdjust) {
      const bounds = markers.map(m => [m.lat, m.lng] as [number, number])
      // try/catch: se o container ainda estiver com dimensão inválida (transição
      // do box), fitBounds lança — não pode derrubar o mapa (2026-07-14).
      try {
        if (bounds.length === 1) {
          map.setView(bounds[0], 15)
        } else if (bounds.length > 1) {
          // Padding assimétrico: a barra de controles (256px) e o Painel
          // Executivo (340px) flutuam POR CIMA do mapa. Com padding simétrico o
          // fitBounds usa a viewport inteira e joga metade dos pins embaixo dos
          // painéis — a faixa visível fica vazia e parece que o filtro apagou
          // tudo (relato de 18/08 ao trocar de Estado).
          map.fitBounds(bounds, { paddingTopLeft: [285, 20], paddingBottomRight: [365, 20], maxZoom: 14 })
        }
        lastCountRef.current = markers.length
        console.log(`[MAPA] Bounds ajustados para ${markers.length} marcadores`)
      } catch (e) {
        console.warn('[MAPA] fitBounds adiado (container instável):', e)
      }
    }
  }, [markers.length, map, resetKey, enabled])

  return null
}

// Leaflet mede o container UMA vez no init. Desde o encaixotamento (2026-07-14)
// o mapa vive dentro de um box com respiro (30px) que muda o tamanho após o
// mount — sem invalidateSize os tiles renderizam no tamanho errado e o mapa
// "trava"/dá erro (fitBounds sobre dimensão inválida). Este handler força o
// recálculo assim que o layout do box assenta e sempre que o container muda de
// tamanho (sidebar recolhe, resize da janela, etc.).
function MapResizeHandler() {
  const map = useMap()
  useEffect(() => {
    const fix = () => { try { map.invalidateSize() } catch { /* mapa desmontando */ } }
    const t0 = setTimeout(fix, 0)
    const t1 = setTimeout(fix, 300)
    const ro = new ResizeObserver(fix)
    ro.observe(map.getContainer())
    return () => { clearTimeout(t0); clearTimeout(t1); ro.disconnect() }
  }, [map])
  return null
}

// Enquadra o mapa nos limites da cobertura por município quando a camada liga.
// Sem isso o overlay renderiza mas fica INVISÍVEL na prática: o Mapa de Ativas
// abre no zoom 12 (nível de rua) dentro de UM município só — a cor de
// preenchimento é sutil (opacity 0.3) e as bordas (onde dá pra perceber
// verde×cinza) ficam a dezenas de km de distância, fora da tela.
function MunicipioCoverageBoundsAdjuster({ active, geoData }: { active: boolean; geoData: { features: any[] } | null }) {
  const map = useMap()
  const firedRef = useRef(false)
  useEffect(() => {
    if (!active) { firedRef.current = false; return }
    if (!geoData || geoData.features.length === 0 || firedRef.current) return
    firedRef.current = true
    try {
      const bounds = L.geoJSON(geoData as any).getBounds()
      if (bounds.isValid()) map.fitBounds(bounds, { padding: [40, 40] })
    } catch { /* geometria inválida — mantém a visão atual */ }
  }, [active, geoData, map])
  return null
}

// Cores dos inativos (pastel — flat, sem sombra, mesmo formato de pin das ativas)
const INATIVO_COLORS: Record<string, string> = {
  '1': '#fcd34d', // âmbar claro (empresário) — mesma família do pin ativo
  '2': '#d8b4fe', // roxo claro (líder) — mesma família do pin ativo
  '3': '#7dd3fc', // azul claro (consultora) — mesma família do pin ativo
}
const INATIVO_TIPO_NOMES: Record<string, string> = {
  '1': 'Empresário',
  '2': 'Líder',
  '3': 'Consultora',
}

// Escapa texto do banco que vai para HTML montado à mão (popup de município,
// via L.popup().setContent). `cidade` vem do cadastro sincronizado do Oracle.
const escHtmlMapa = (v: unknown) =>
  String(v ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')

// Cache de ícones DivIcon para inativos (1 por tipo — reutilizado por todos os markers)
const INATIVO_ICON_CACHE: Record<string, DivIcon> = {}
const getInativoIcon = (tipo: string): DivIcon => {
  if (!INATIVO_ICON_CACHE[tipo]) {
    INATIVO_ICON_CACHE[tipo] = createColoredIcon(INATIVO_COLORS[tipo] || INATIVO_COLORS['3'])
  }
  return INATIVO_ICON_CACHE[tipo]
}

// Overlay de inativos — Pins iguais às ativas (DivIcon SVG)
// Renderização progressiva na 1ª vez; toggle ON/OFF instantâneo (markers ficam em memória)
const InativosOverlay = React.memo(({
  clientes,
  show,
  onProgress
}: {
  clientes: Array<{ codigo: string; latitude: number | null; longitude: number | null; tipo_consultor: string | null; nome: string; endereco_cidade: string; cidade: string; cep: string }>
  show: boolean
  onProgress?: (pct: number | null) => void
}) => {
  const map = useMap()
  const layerGroupRef = useRef<L.FeatureGroup | null>(null)
  const timeoutsRef = useRef<NodeJS.Timeout[]>([])
  const isAnimatingRef = useRef(false)
  const renderedDataKeyRef = useRef('') // chave dos dados já renderizados

  // Agrupar clientes por cidade, ordenar por tamanho (menor→maior), capital (Goiânia) por último
  const cidadesAgrupadas = useMemo(() => {
    if (clientes.length === 0) return []

    const groups = new Map<string, typeof clientes>()
    for (const c of clientes) {
      if (!c.latitude || !c.longitude) continue
      const cidade = (c.endereco_cidade || c.cidade || 'Sem cidade').toUpperCase().trim()
      if (!groups.has(cidade)) groups.set(cidade, [])
      groups.get(cidade)!.push(c)
    }

    // Ordena por tamanho: a maior (capital) fica por último no desenho.
    return Array.from(groups.entries()).sort((a, b) => a[1].length - b[1].length)
  }, [clientes])

  const dataKey = `${clientes.length}-${cidadesAgrupadas.length}`

  // EFEITO 1: Inicializar cluster group (1 vez) — clusteriza os inativos, removendo
  // o antigo teto de 3.000 pins e o lag. chunkedLoading evita travar a UI ao adicionar.
  useEffect(() => {
    layerGroupRef.current = (L as any).markerClusterGroup({
      chunkedLoading: true,
      maxClusterRadius: 50,
      spiderfyOnMaxZoom: true,
      showCoverageOnHover: false,
      removeOutsideVisibleBounds: true,
      iconCreateFunction: (cluster: any) => {
        const count = cluster.getChildCount()
        const size = count < 100 ? 36 : count < 500 ? 44 : 52
        return new DivIcon({
          html: `<div style="display:flex;align-items:center;justify-content:center;width:${size}px;height:${size}px;border-radius:50%;background:rgba(148,163,184,0.92);color:#fff;font-weight:700;font-size:12px;border:3px solid rgba(255,255,255,0.85);box-shadow:0 2px 8px rgba(0,0,0,0.3)">${count}</div>`,
          className: 'mapa-inativo-cluster',
          iconSize: [size, size],
        })
      },
    }) as L.FeatureGroup
    return () => {
      if (layerGroupRef.current) {
        layerGroupRef.current.clearLayers()
        if (map.hasLayer(layerGroupRef.current)) map.removeLayer(layerGroupRef.current)
        layerGroupRef.current = null
      }
    }
  }, [map])

  // EFEITO 2: Mostrar/esconder layer group (INSTANTÂNEO — sem recriar markers)
  useEffect(() => {
    const lg = layerGroupRef.current
    if (!lg) return

    if (show) {
      // Adicionar ao mapa (markers já estão no layer group se carregados)
      if (!map.hasLayer(lg)) map.addLayer(lg)
    } else {
      // Remover do mapa SEM destruir os markers — ficam em memória
      if (map.hasLayer(lg)) map.removeLayer(lg)
      // Cancelar animação em andamento se houver
      timeoutsRef.current.forEach(t => clearTimeout(t))
      timeoutsRef.current = []
      if (isAnimatingRef.current) {
        isAnimatingRef.current = false
        onProgress?.(null)
      }
    }
  }, [map, show, onProgress])

  // EFEITO 3: Carregar markers (só roda se dados mudarem ou nunca carregou)
  useEffect(() => {
    const lg = layerGroupRef.current
    if (!lg || !show || cidadesAgrupadas.length === 0) return

    // ✅ CACHE HIT — markers já estão em memória, não recriar
    if (renderedDataKeyRef.current === dataKey && lg.getLayers().length > 0) {
      console.log(`[INATIVOS] ⚡ Cache hit — ${lg.getLayers().length} pins já em memória`)
      return
    }

    // Cache miss — limpar e carregar com animação cidade por cidade
    lg.clearLayers()
    onProgress?.(0)

    isAnimatingRef.current = true
    let cityIdx = 0
    let totalMarkers = 0

    const renderNextCity = () => {
      if (cityIdx >= cidadesAgrupadas.length) {
        isAnimatingRef.current = false
        renderedDataKeyRef.current = dataKey // Marcar como carregado
        onProgress?.(100)
        setTimeout(() => onProgress?.(null), 800)
        console.log(`[INATIVOS] ✅ Completo: ${totalMarkers} pins em ${cityIdx} cidades (em memória)`)
        return
      }

      const [cidadeNome, cidadeClientes] = cidadesAgrupadas[cityIdx]
      cityIdx++
      let cityCount = 0

      for (const c of cidadeClientes) {
        if (!c.latitude || !c.longitude) continue

        const tipo = c.tipo_consultor || '3'
        const marker = L.marker([c.latitude, c.longitude], {
          icon: getInativoIcon(tipo),
          interactive: true
        })

        // Popup lazy — criado apenas no clique
        marker.on('click', () => {
          const tipoNome = INATIVO_TIPO_NOMES[tipo] || 'Consultora'
          L.popup({ closeButton: false, offset: [0, -28] })
            .setLatLng([c.latitude!, c.longitude!])
            .setContent(
              `<div style="font-size:12px;min-width:150px">` +
              `<div style="font-weight:600;color:${INATIVO_COLORS[tipo] || '#93c5fd'}">${tipoNome} — Inativo</div>` +
              `<div style="font-weight:500;margin-top:2px">${c.nome}</div>` +
              `<div style="color:#9ca3af;margin-top:2px">${c.endereco_cidade || c.cidade || ''}</div>` +
              `<div style="color:#9ca3af;font-size:11px">CEP: ${c.cep || 'N/A'}</div>` +
              `</div>`
            )
            .openOn(map)
        })

        lg.addLayer(marker)
        totalMarkers++
        cityCount++
      }

      if (cityCount > 0) {
        const pct = Math.round((cityIdx / cidadesAgrupadas.length) * 100)
        onProgress?.(pct)
        console.log(`[INATIVOS] 🏙️ ${cidadeNome}: +${cityCount} pins (total: ${totalMarkers}) [${pct}%]`)
      }

      // Delay proporcional ao tamanho da cidade — visual de "acender"
      const delay = cityCount > 100 ? 180 : cityCount > 30 ? 100 : cityCount > 5 ? 50 : 20
      const t = setTimeout(renderNextCity, delay)
      timeoutsRef.current.push(t)
    }

    // Iniciar após breve delay para não travar o toggle
    const t = setTimeout(renderNextCity, 30)
    timeoutsRef.current.push(t)

    return () => {
      timeoutsRef.current.forEach(t => clearTimeout(t))
      timeoutsRef.current = []
      isAnimatingRef.current = false
    }
  }, [map, cidadesAgrupadas, show, dataKey, onProgress])

  return null
})

// Marcador de ativa — React.memo para evitar 1000+ re-renders desnecessários.
// O clique abre o painel flutuante de detalhes (padrão do painel Rede) — o
// Popup do Leaflet saiu na padronização visual de 2026-07-08. ATENÇÃO: o
// comparador ignora eventHandlers de propósito (identidade muda a cada
// render); handlers que dependem de estado devem ler via ref, não do closure.
const AtivaMarker = React.memo(({
  position,
  icon,
  draggable,
  eventHandlers,
  opacity,
  zIndexOffset
}: {
  position: [number, number]
  icon: any
  draggable?: boolean
  eventHandlers?: any
  opacity?: number
  zIndexOffset?: number
}) => (
  <LeafletMarker position={position} icon={icon} draggable={draggable} eventHandlers={eventHandlers} opacity={opacity ?? 1} zIndexOffset={zIndexOffset ?? 0} />
), (prevProps, nextProps) => {
  // Só re-renderizar se position, icon, draggable, opacity ou zIndexOffset
  // mudarem (opacity/zIndexOffset entraram pro isolamento da camada
  // Organização — clicar numa líder esmaece o resto).
  return (
    prevProps.position[0] === nextProps.position[0] &&
    prevProps.position[1] === nextProps.position[1] &&
    prevProps.icon === nextProps.icon &&
    prevProps.draggable === nextProps.draggable &&
    prevProps.opacity === nextProps.opacity &&
    prevProps.zIndexOffset === nextProps.zIndexOffset
  )
})

// Normaliza texto para busca (remove acentos, maiúsculas) — usado pelo filtro
// de busca no painel pai e pela lista de resultados (fora do Leaflet).
const normalizeText = (text: string) => {
  return text
    .toUpperCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .trim()
}

// Tipo de pessoa → label/cor, usado pela lista de resultados da busca.
const getTipoLabel = (tipo?: string | null) => {
  switch (tipo) {
    case '1': return { label: 'Empresário', color: '#f59e0b' }
    case '2': return { label: 'Líder', color: '#a855f7' }
    case '3': return { label: 'Consultora', color: '#0ea5e9' }
    default: return { label: 'Pessoa', color: '#666' }
  }
}

// Navega o mapa (flyTo) quando o alvo de foco muda — busca e legenda-toggle
// agora vivem na barra única (fora do Leaflet); este é só o "braço" que
// aciona a câmera. Sem estado próprio, sem zoom automático por tecla.
const MapFlyTo = ({ target }: { target: { lat: number; lng: number; zoom: number } | null }) => {
  const map = useMap()
  useEffect(() => {
    if (target) map.flyTo([target.lat, target.lng], target.zoom, { duration: 1.2 })
  }, [target, map])
  return null
}

// Isolamento da camada Organização — clicar numa líder foca ela + seus
// consultores (flyToBounds) e esmaece o resto; clicar fora volta pra visão
// geral. Mesmo padrão do painel Rede (NetworkFocusEffect em
// LeaderCoverageView.tsx). flyingRef evita que o clique-fora dispare no meio
// da animação (senão fecha sozinho — bug real já visto na Rede).
function OrgFocusEffect({ focusPositions, allPositions, onBackgroundClick }: { focusPositions: [number, number][] | null; allPositions: [number, number][]; onBackgroundClick: () => void }) {
  const map = useMap()
  const wasFocusedRef = useRef(false)
  // Bloqueia o clique-de-fundo SÓ durante os voos programáticos DESTE efeito.
  // A versão anterior ouvia movestart/moveend globais — qualquer pan/zoom do
  // usuário armava o bloqueio e engolia o clique seguinte ("clique fora só
  // funciona de vez em quando", bug real reportado em 2026-07-08).
  const flyingRef = useRef(false)
  const flyTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const flyTo = useCallback((positions: [number, number][], opts: any) => {
    flyingRef.current = true
    if (flyTimeoutRef.current) clearTimeout(flyTimeoutRef.current)
    map.once('moveend', () => { flyingRef.current = false })
    // Fallback: se os bounds já são a visão atual, moveend pode nunca vir
    flyTimeoutRef.current = setTimeout(() => { flyingRef.current = false }, 900)
    map.flyToBounds(positions, opts)
  }, [map])
  useEffect(() => {
    if (focusPositions && focusPositions.length > 0) {
      wasFocusedRef.current = true
      flyTo(focusPositions, { padding: [70, 70], maxZoom: 15, duration: 0.4 })
    } else if (wasFocusedRef.current) {
      wasFocusedRef.current = false
      if (allPositions.length > 0) flyTo(allPositions, { padding: [40, 40], duration: 0.4 })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, focusPositions])
  useEffect(() => {
    const handler = () => { if (!flyingRef.current) onBackgroundClick() }
    map.on('click', handler)
    return () => { map.off('click', handler) }
  }, [map, onBackgroundClick])
  return null
}

// Esmaece o mapa-base (tiles) durante o isolamento da Organização — o grupo
// focado salta do fundo fosco. Só o tilePane: pins e linhas têm canais de
// opacidade próprios (esmaecer o overlayPane apagaria também o grupo focado).
function FocusFade({ active }: { active: boolean }) {
  const map = useMap()
  useEffect(() => {
    const pane = map.getPane('tilePane')
    if (!pane) return
    pane.style.transition = 'filter 0.3s ease, opacity 0.3s ease'
    // classe em vez de filter inline: inline SOBRESCREVE o filtro de tile do
    // tema e o mapa perdia o tratamento neu durante todo o isolamento.
    pane.classList.toggle('neu-tile-dim', !!active)
    pane.style.opacity = active ? '0.35' : '1'
    return () => { pane.classList.remove('neu-tile-dim'); pane.style.opacity = '1' }
  }, [map, active])
  return null
}

// Icone de pin SVG customizado
// PinIcon removido - não mais utilizado com Leaflet

// GeoGeometry e GeoFeature removidos - não mais necessários com Leaflet

interface MapMarker {
  id: string
  municipioId: string
  name: string
  coordinates: [number, number]
  data?: {
    recrutas?: number
    vendas?: number
    status?: 'active' | 'inactive'
  }
}

interface ClienteData {
  codigo: string
  nome: string
  cidade: string
  endereco_cidade: string
  cep: string
  latitude: number | null
  longitude: number | null
  distrito_codigo: string | null
  distrito_nome: string | null
  grupo_codigo: string | null
  grupo_nome: string | null
  tipo_consultor: string | null // '2' = Líder, '3' = Consultor
  telefone1: string | null
  celular: string | null
  email: string | null
  created_at: string
}

interface UploadProgressData {
  stage: string
  percent: number
  detail: string
}

interface GeocodingStatus {
  isGeocoding: boolean
  geocodingProgress: {
    total: number
    processados: number
    sucesso: number
  }
  clientes: {
    total: number
    comCoordenadas: number
    semCoordenadas: number
  }
  percentual: number
}

// Interface para Ativas da Semana
interface AtivaData {
  codigo: string
  nome: string
  tipo_consultor: string // '1' = Empresário, '2' = Líder, '3' = Consultora
  telefone: string | null
  cep: string | null
  latitude: number | null
  longitude: number | null
  semana: string | null
  profile_pic?: string | null
  cidade?: string | null
  // UF do cadastro (DRADBA.CIDADES.CIDESTADO, gravada pelo sync). Eixo do filtro
  // por Estado da barra de controles.
  uf?: string | null
  endereco?: string | null
  bairro?: string | null
  endereco_numero?: string | null
  email?: string | null
  geo_confidence?: string | null
  geo_source?: string | null
  // Organização real (líder que a consultora responde de fato — não confundir
  // com /api/leaders, que é o cadastro de líderes do fluxo de encaminhamento
  // de leads novos). Uma líder (tipo_consultor='2') é dona de um grupo_codigo;
  // suas consultoras (tipo_consultor='3') compartilham o mesmo grupo_codigo.
  grupo_codigo?: string | null
  grupo_nome?: string | null
  distrito_codigo?: string | null
  distrito_nome?: string | null
  // Valor vendido no período "vitrine" (semana atual + 3 anteriores, mesma
  // janela que decide se está ativa — ver syncAtivasFromOracle no backend).
  // Vem pronto do Oracle (SUM(PEVENDANORMAL)), sem cálculo novo no frontend.
  valor_venda_vitrine?: number | null
}

interface AtivasGeocodingStatus {
  status: string
  statusMessage: string
  isGeocoding: boolean
  progress: {
    total: number
    processados: number
    sucesso: number
  }
  total: number
  comCoordenadas: number
  semCoordenadas: number
  comCep: number
  semCep: number
  percentual: number
  errosCount: number
  hasError: boolean
  hasWarning: boolean
}

interface GeocodingError {
  codigo: string
  nome: string
  cep: string
  cidade?: string
  uf?: string
  erro: string
}

// Interface para semanas de ativas
interface AtivaSemana {
  semana_codigo: string
  nome: string
  total: number
  com_coordenadas: number
  created_at: string
}

interface MapaDistribuicaoProps {
  markers?: MapMarker[]
  onMunicipioClick?: (municipio: { id: string; name: string; coordinates: [number, number] }) => void
  onAddMarker?: (marker: MapMarker) => void
  onRemoveMarker?: (markerId: string) => void
  className?: string
  authenticatedFetch?: (url: string, options?: RequestInit) => Promise<Response>
}

// SVGMap relacionado - removido, agora usando apenas Leaflet

// Cores e labels por tipo de consultor
const TIPO_CONFIG: Record<string, { color: string; label: string }> = {
  '1': { color: '#f59e0b', label: 'EMPRESÁRIO (C1)' },
  '2': { color: '#a855f7', label: 'LÍDER (C2)' },
  '3': { color: '#0ea5e9', label: 'Consultora (C3)' },
}

const formatBRL = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 })

// Detalhe sob demanda de uma ativa (GET /api/mapa/ativas/:codigo/detalhe) —
// vendas por semana da vitrine (Oracle live) e, pra líder, a equipe do grupo.
interface AtivaDetalhe {
  vendas: {
    porSemana: Array<{ semana: string; pedidos: number; valor: number }>
    totalVitrine: number
    ultimoPedido: string | null
  } | null
  vendasErro?: string | null
  equipe: {
    ativas: Array<{ codigo: string; nome: string; valor_venda_vitrine: number; semana_ativa: string | null; cidade: string | null }>
    inativas: Array<{ codigo: string; nome: string; ultima_semana_ativa: string | null; cidade: string | null }>
    totalInativas: number
  } | null
  // Só pra empresária (C1): hierarquia do distrito dela — líderes ativas com
  // rollup da equipe de cada uma + totais de consultoras.
  estrutura: {
    distrito: string
    lideres: Array<{ codigo: string; nome: string; cidade: string | null; semana_ativa: string | null; valor_venda_vitrine: number; equipeCount: number; equipeValor: number }>
    totais: { lideres: number; consultoras: number; valorConsultoras: number; consultorasSemLiderAtiva: number; inativas: number }
  } | null
}

const fmtDataHora = (iso: string) =>
  new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })

// '2026-W27' → 'W27' pras linhas compactas de equipe/vendas (title tem o cheio)
const semanaCurta = (code: string | null | undefined) => (code ? code.replace(/^\d{4}-/, '') : '—')

// Cache de clientes em memória — persiste entre trocas de tela
let _clientesCache: ClienteData[] = []

const MapaDistribuicao = ({
  markers: _markers = [],
  onMunicipioClick: _onMunicipioClick,
  onAddMarker: _onAddMarker,
  onRemoveMarker: _onRemoveMarker,
  className = '',
  authenticatedFetch
}: MapaDistribuicaoProps) => {
  // Nota: onAddMarker e onRemoveMarker são opcionais - clique no município abre modal de consultores
  void _markers // Props opcionais não utilizados diretamente
  void _onAddMarker
  void _onRemoveMarker

  // Key para forçar reset do ZoomableGroup apenas quando explicitamente necessário
  const [mapResetKey, setMapResetKey] = useState(0)

  // Ref para authenticatedFetch para evitar re-renders quando a função muda
  const authenticatedFetchRef = useRef(authenticatedFetch)
  authenticatedFetchRef.current = authenticatedFetch

  // Estados para upload e clientes
  const [clientes, setClientes] = useState<ClienteData[]>(_clientesCache)
  const [isUploading, setIsUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState<UploadProgressData | null>(null)
  const [showMappingModal, setShowMappingModal] = useState(false)
  const [mappingResult, setMappingResult] = useState<any>(null)
  const [pendingFile, setPendingFile] = useState<File | null>(null)
  const [showAdminSection, setShowAdminSection] = useState(false) // seção "Administração" colapsável na barra única (upload de clientes, sync Oracle, geocodificação)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [geocodingStatus, setGeocodingStatus] = useState<GeocodingStatus | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const geocodingPollRef = useRef<NodeJS.Timeout | null>(null)

  // Estados para Ativas da Semana
  const [ativas, setAtivas] = useState<AtivaData[]>([])
  const [ativasGeocodingStatus, setAtivasGeocodingStatus] = useState<AtivasGeocodingStatus | null>(null)
  const [geocodingErrors, setGeocodingErrors] = useState<GeocodingError[]>([])
  const [showErrorsModal, setShowErrorsModal] = useState(false)
  const [syncStatus, setSyncStatus] = useState<{ isSyncing: boolean; lastResult: any; config: any; nextSyncAt: string | null } | null>(null)
  const syncPollRef = useRef<NodeJS.Timeout | null>(null)
  const ativasGeocodingPollRef = useRef<NodeJS.Timeout | null>(null)

  // Cores fixas dos pins ATIVAS (padronizadas com mapa externo)
  const empresarioColor = '#f59e0b'
  const leaderColor = '#a855f7'
  const consultorColor = '#0ea5e9'

  // Estados para mapa de calor e visibilidade de camadas
  const [showHeatmap, setShowHeatmap] = useState(false)
  const [showEmpresarios, setShowEmpresarios] = useState(true)
  const [showLideres, setShowLideres] = useState(true)
  const [showConsultoras, setShowConsultoras] = useState(true)
  const [showInativos, setShowInativos] = useState(false) // Toggle para mostrar clientes inativos no mapa de ativas
  const [inativosProgress, setInativosProgress] = useState<number | null>(null) // 0-100 durante carregamento, null quando idle/completo
  // Gate do primeiro enquadramento: com Estado salvo em user_mapa_preferences o
  // mapa abria na praça inteira e só depois pulava pro recorte.
  const [preferencesLoaded, setPreferencesLoaded] = useState(false)
  const [isPresentationMode, setIsPresentationMode] = useState(false)
  const mapContainerRef = useRef<HTMLDivElement>(null)

  // Painel flutuante de detalhes (padrão do painel Rede, LeaderCoverageView) —
  // substitui o Popup do Leaflet nos pins de ativas. Abre no clique do pin ou
  // de um resultado da busca; fecha no X, Escape ou clique no fundo do mapa.
  // O detalhe (vendas por semana + equipe da líder) é buscado sob demanda.
  const [ativaDetail, setAtivaDetail] = useState<AtivaData | null>(null)
  const [detalhe, setDetalhe] = useState<AtivaDetalhe | null>(null)
  const [detalheLoading, setDetalheLoading] = useState(false)

  // Busca — estado vive aqui (fora do Leaflet) desde a unificação da barra,
  // igual ao padrão do painel Rede. `mapFocus` é o único canal de navegação
  // pro mapa: muda só no clique de um resultado ou nas setas ↑/↓, nunca a
  // cada tecla digitada (era isso que fazia a câmera "voar" sem parar).
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedSearchIndex, setSelectedSearchIndex] = useState(0)
  const [mapFocus, setMapFocus] = useState<{ lat: number; lng: number; zoom: number } | null>(null)
  const [mapCfg, setMapCfg] = useState<{ center?: { lat: number; lng: number; zoom: number }; capital?: string; geojsonUrl?: string; bounds?: any } | null>(null)
  const DEFAULT_MAP_CENTER = mapCfg?.center || { lat: -14.5, lng: -51.5, zoom: 4 }

  // Tema (claro/escuro) → alterna o estilo do basemap próprio.
  // A URL é relativa (/tiles/...): quem decide o destino real é o nginx da
  // stack basemap, e é isso que permite voltar atrás sem rebuild.
  const isDark = useIsDark()
  const tileUrl = basemapUrl(isDark)

  // Camada de confiança: filtro de triagem "só baixa confiança"
  const [onlyLowConfidence, setOnlyLowConfidence] = useState(false)

  // MOVER PIN: só admin, e só com o modo ligado de propósito.
  //
  // Antes, TODO marcador nascia `draggable` sem checagem nenhuma: qualquer
  // usuário autenticado reposicionava qualquer ativa e a gravação entrava como
  // `manual`/alta confiança — passando por cima do geocodificador e sem deixar
  // rastro de quem foi. E como o pin ocupa 16px, um simples pan do mapa que
  // começasse em cima de um deles virava arrasto acidental.
  const { user } = useAuth()
  const podeAjustarPin = !!user?.isAdmin
  const [modoAjustePin, setModoAjustePin] = useState(false)
  const pinArrastavel = podeAjustarPin && modoAjustePin

  // Organização real (permanente) — liga cada consultora ativa à líder que
  // é dona do MESMO grupo_codigo (vínculo de negócio real, não geografia).
  // Padrão trazido do painel Rede (LeaderCoverageView.tsx: camada líder×lead
  // permanente, ver memória project-crm-twms-rede-panel). Nem toda
  // consultora resolve — a líder do grupo dela pode não estar ativa nessa
  // mesma semana (~35% dos casos, confirmado no dado real); pra essas,
  // simplesmente não há linha. É a camada principal deste painel — mostra
  // quem são as líderes e seus consultores ativos da semana (o cadastro de
  // líderes por raio/proximidade pro roteamento de leads novos foi removido
  // daqui por pedido do dono — não se usa nesse contexto).
  const [showOrgLinks, setShowOrgLinks] = useState(true)
  // Ref-espelho pro handler de clique do marker (memoizado com comparador que
  // ignora eventHandlers — closure ficaria presa no valor antigo do toggle).
  const showOrgLinksRef = useRef(showOrgLinks)
  showOrgLinksRef.current = showOrgLinks
  // Isolamento — clicar numa líder (com a camada ligada) foca ela + seus
  // consultores e esmaece o resto, igual ao painel Rede (NetworkFocusEffect).
  // Guarda o grupo_codigo focado; null = sem isolamento.
  const [orgFocusGrupo, setOrgFocusGrupo] = useState<string | null>(null)

  // Overlay de cobertura por município (verde=com líder / cinza=sem líder,
  // área a desbloquear) — MESMO endpoint e GeoJSON do painel Rede
  // (/api/leader-coverage + public/geo/ms-municipios.geojson). Ligada por
  // padrão (pedido do dono) — continua um toggle, só não parte desligada.
  const [showMunicipioCoverage, setShowMunicipioCoverage] = useState(true)
  // A camada nasce ligada, então o fitBounds dela roubava o enquadramento inicial
  // dos pins. Só reenquadra pela cobertura depois que o usuário mexe no toggle.
  const [coberturaLigadaPeloUsuario, setCoberturaLigadaPeloUsuario] = useState(false)
  const [municipioCoverageData, setMunicipioCoverageData] = useState<Array<{ cidade: string; consultoras: number; lideres: number; consultorasPorLider: number | null; gap: boolean; lat: number | null; lng: number | null }> | null>(null)
  const [municipioGeoFeatures, setMunicipioGeoFeatures] = useState<any[] | null>(null)
  const [municipioCoverageLoaded, setMunicipioCoverageLoaded] = useState(false)

  // Arrastar pin pra corrigir: confirmação pendente com endereço reverso resolvido
  const [dragConfirm, setDragConfirm] = useState<{ ativa: AtivaData; lat: number; lng: number; address: string; loading: boolean } | null>(null)
  // Epoch dos marcadores de ativas — bump força remount (snap-back no cancelar / reposição no salvar)
  const [markerEpoch, setMarkerEpoch] = useState(0)


  // Estados para edição de cadastro via mapa
  const [editingAtiva, setEditingAtiva] = useState<AtivaData | null>(null)
  const [editFormData, setEditFormData] = useState<Record<string, string>>({})
  const [isSavingEdit, setIsSavingEdit] = useState(false)
  const [editSaveError, setEditSaveError] = useState<string | null>(null)

  // Estados para lista de semanas
  const [semanas, setSemanas] = useState<AtivaSemana[]>([])
  const [semanaSelecionada, setSemanaSelecionada] = useState<string | null>(null)
  // Recorte por Estado. É PERSISTIDO por usuário em user_mapa_preferences.mapa_uf
  // (quem cuida de uma região só abre já nela). O risco de abrir com número menor
  // sem entender por quê é coberto pelo chip aceso na barra e pela tarja no
  // Painel Executivo.
  const [ufSelecionada, setUfSelecionada] = useState<string | null>(null)
  // Painel de controles (busca/semanas/camadas) recolhível — pedido da diretoria
  // 2026-07-14: liberar a visão do mapa; mesmo padrão do ExecutivoPanel.
  const [painelAberto, setPainelAberto] = useState(true)
  const [editingSemana, setEditingSemana] = useState<string | null>(null)
  const [editingNome, setEditingNome] = useState('')

  // Fetch helper - usa ref para evitar re-renders quando authenticatedFetch muda
  const fetchApi = useCallback(async (url: string, options?: RequestInit) => {
    if (authenticatedFetchRef.current) {
      return authenticatedFetchRef.current(url, options)
    }
    return fetch(url, options)
  }, []) // Sem dependências - usa ref internamente

  // Config da praça (região auto-detectada no backend): centro/zoom do mapa e
  // URL do choropleth. Sem geografia hardcoded no frontend — funciona pra
  // qualquer praça só plugando o DRACO.
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const r = await fetchApi('/api/mapa/config')
        const j = await r.json()
        if (!cancelled && j && !j.error) setMapCfg(j)
      } catch { /* fallback: centro do Brasil */ }
    })()
    return () => { cancelled = true }
  }, [fetchApi])


  // Detalhe da ativa selecionada — busca sob demanda ao abrir o painel
  // (vendas por semana via Oracle live + equipe do grupo pra líder).
  useEffect(() => {
    if (!ativaDetail) { setDetalhe(null); return }
    let cancelled = false
    setDetalhe(null)
    setDetalheLoading(true)
    ;(async () => {
      try {
        const res = await fetchApi(API_ENDPOINTS.MAPA_ATIVAS_DETALHE(ativaDetail.codigo))
        if (!res.ok) throw new Error('detalhe indisponível')
        const data = await res.json()
        if (!cancelled) setDetalhe(data)
      } catch {
        if (!cancelled) setDetalhe(null)
      } finally {
        if (!cancelled) setDetalheLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [ativaDetail?.codigo, fetchApi]) // eslint-disable-line react-hooks/exhaustive-deps

  // Escape fecha o painel de detalhes (o modal de edição tem o próprio handler)
  useEffect(() => {
    if (!ativaDetail || editingAtiva) return
    const handleEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') setAtivaDetail(null) }
    window.addEventListener('keydown', handleEsc)
    return () => window.removeEventListener('keydown', handleEsc)
  }, [ativaDetail, editingAtiva])

  // Nome amigável da semana (chips permitem renomear; linhas usam o nome se houver)
  const semanaNome = useCallback((code: string | null | undefined) => {
    if (!code) return '—'
    return semanas.find(s => s.semana_codigo === code)?.nome || code
  }, [semanas])

  // Carregar clientes
  const loadClientes = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const response = await fetchApi(API_ENDPOINTS.MAPA_CLIENTES)
      if (!response.ok) throw new Error('Erro ao carregar clientes')
      const data = await response.json()
      // Só contagens no console. Antes daqui saía o objeto completo de um
      // cliente e a lista nominal das líderes (nome + cidade) — dado pessoal
      // no console do browser, em produção.
      const lista = data.clientes || []
      if (import.meta.env.DEV) {
        console.log('[MAPA] clientes:', lista.length,
          '| líderes:', lista.filter((c: ClienteData) => c.tipo_consultor === '2').length)
      }
      _clientesCache = lista  // Salvar no cache de memória
      setClientes(lista)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro desconhecido')
    } finally {
      setIsLoading(false)
    }
  }, [fetchApi])

  // Verificar status da geocodificação
  const checkGeocodingStatus = useCallback(async () => {
    try {
      const response = await fetchApi('/api/mapa/geocoding-status')
      if (!response.ok) return null
      const status: GeocodingStatus = await response.json()
      setGeocodingStatus(status)
      return status
    } catch {
      return null
    }
  }, [fetchApi])

  // Função removida: processNextBatch (modo de lote automático desabilitado)

  // Handler para abrir modal de edição a partir do popup do mapa
  const handleOpenEditModal = useCallback((ativa: AtivaData) => {
    setEditingAtiva(ativa)
    setEditFormData({
      nome: ativa.nome || '',
      celular: ativa.telefone || '',
      email: ativa.email || '',
      endereco_cep: ativa.cep || '',
      endereco_logradouro: ativa.endereco || '',
      endereco_numero: ativa.endereco_numero || '',
      endereco_bairro: ativa.bairro || '',
      endereco_cidade: ativa.cidade || '',
    })
    setEditSaveError(null)
  }, [])

  // Handler para salvar edição de cadastro
  const handleSaveEdit = useCallback(async () => {
    if (!editingAtiva || isSavingEdit) return
    setIsSavingEdit(true)
    setEditSaveError(null)

    try {
      const response = await fetchApi(API_ENDPOINTS.MAPA_CADASTRO_UPDATE(editingAtiva.codigo), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editFormData),
      })

      if (!response.ok) {
        const err = await response.json()
        throw new Error(err.error || 'Erro ao salvar')
      }

      const data = await response.json()
      const updated = data.cadastro

      // Atualizar estado local para refletir mudanças sem reload
      const applyUpdate = (a: AtivaData): AtivaData => ({
        ...a,
        nome: updated.nome || a.nome,
        telefone: updated.telefone || a.telefone,
        cep: updated.cep || a.cep,
        cidade: updated.cidade || a.cidade,
        endereco: updated.endereco || a.endereco,
        bairro: updated.bairro || a.bairro,
        latitude: updated.latitude !== null ? parseFloat(updated.latitude) : null,
        longitude: updated.longitude !== null ? parseFloat(updated.longitude) : null,
      })
      setAtivas(prev => prev.map(a => a.codigo === editingAtiva.codigo ? applyUpdate(a) : a))
      // Painel de detalhes aberto pra mesma pessoa acompanha a edição
      setAtivaDetail(prev => prev && prev.codigo === editingAtiva.codigo ? applyUpdate(prev) : prev)

      setEditingAtiva(null)
    } catch (err) {
      setEditSaveError(err instanceof Error ? err.message : 'Erro desconhecido')
    } finally {
      setIsSavingEdit(false)
    }
  }, [editingAtiva, editFormData, fetchApi, isSavingEdit])

  // Fechar modal com Escape
  useEffect(() => {
    if (!editingAtiva) return
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setEditingAtiva(null)
    }
    window.addEventListener('keydown', handleEsc)
    return () => window.removeEventListener('keydown', handleEsc)
  }, [editingAtiva])

  // Carregar ativas da semana
  const loadAtivas = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const response = await fetchApi(API_ENDPOINTS.MAPA_ATIVAS)
      if (!response.ok) throw new Error('Erro ao carregar ativas')
      const data = await response.json()
      console.log('[MAPA] Ativas carregadas:', data.total)
      setAtivas(data.ativas || [])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro desconhecido')
    } finally {
      setIsLoading(false)
    }
  }, [fetchApi])

  // Verificar status da geocodificação de ativas
  const checkAtivasGeocodingStatus = useCallback(async () => {
    try {
      const response = await fetchApi(API_ENDPOINTS.MAPA_ATIVAS_GEOCODING_STATUS)
      if (!response.ok) return null
      const status: AtivasGeocodingStatus = await response.json()
      setAtivasGeocodingStatus(status)
      return status
    } catch {
      return null
    }
  }, [fetchApi])

  // Carregar log de erros da geocodificação
  const loadGeocodingErrors = useCallback(async () => {
    try {
      const response = await fetchApi(API_ENDPOINTS.MAPA_ATIVAS_GEOCODING_ERRORS)
      if (!response.ok) return
      const data = await response.json()
      setGeocodingErrors(data.errors || [])
    } catch {
      // Silently fail
    }
  }, [fetchApi])

  // Carregar lista de semanas
  const loadSemanas = useCallback(async () => {
    try {
      const response = await fetchApi(API_ENDPOINTS.MAPA_ATIVAS_SEMANAS)
      if (!response.ok) return
      const data = await response.json()
      setSemanas(data.semanas || [])
    } catch (err) {
      console.error('Erro ao carregar semanas:', err)
    }
  }, [fetchApi])

  // Renomear semana
  const handleRenameSemana = useCallback(async (semanaCodigo: string, novoNome: string) => {
    try {
      const response = await fetchApi(API_ENDPOINTS.MAPA_ATIVAS_SEMANA(semanaCodigo), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nome: novoNome })
      })
      if (!response.ok) throw new Error('Erro ao renomear')
      setEditingSemana(null)
      setEditingNome('')
      await loadSemanas()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao renomear')
    }
  }, [fetchApi, loadSemanas])

  // Deletar semana
  const handleDeleteSemana = useCallback(async (semanaCodigo: string) => {
    if (!confirm(`Tem certeza que deseja excluir a semana ${semanaCodigo}?`)) return
    try {
      const response = await fetchApi(API_ENDPOINTS.MAPA_ATIVAS_SEMANA(semanaCodigo), {
        method: 'DELETE'
      })
      if (!response.ok) throw new Error('Erro ao excluir')
      await loadSemanas()
      await loadAtivas()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao excluir')
    }
  }, [fetchApi, loadSemanas, loadAtivas])


  // Carregar clientes ao montar
  useEffect(() => {
    loadClientes()
    checkGeocodingStatus()
    loadAtivas()
    checkAtivasGeocodingStatus()
    loadSemanas()
    checkSyncStatus()
    // Refresh sync status a cada 30s
    syncPollRef.current = setInterval(async () => {
      const status = await checkSyncStatus()
      // Se sync acabou de completar, recarregar dados
      if (status && !status.isSyncing && status.lastResult?.timestamp) {
        const lastSyncAge = Date.now() - new Date(status.lastResult.timestamp).getTime()
        if (lastSyncAge < 35000) { // Concluiu nos últimos 35s (janela do polling)
          await loadAtivas()
          await loadSemanas()
          await checkAtivasGeocodingStatus()
        }
      }
    }, 30000)
    return () => {
      if (syncPollRef.current) clearInterval(syncPollRef.current)
    }
  }, [])

  // Polling para status de geocodificação + processamento em lotes
  useEffect(() => {
    // Iniciar polling se estiver geocodificando
    if (geocodingStatus?.isGeocoding) {
      geocodingPollRef.current = setInterval(async () => {
        const status = await checkGeocodingStatus()
        if (status && !status.isGeocoding) {
          // Parar polling quando terminar
          if (geocodingPollRef.current) {
            clearInterval(geocodingPollRef.current)
            geocodingPollRef.current = null
          }
          // Recarregar clientes
          await loadClientes()
        }
      }, 2000)
    }

    return () => {
      if (geocodingPollRef.current) {
        clearInterval(geocodingPollRef.current)
      }
    }
  }, [geocodingStatus?.isGeocoding, checkGeocodingStatus, loadClientes])

  // Polling para status de geocodificação de ativas - otimizado para evitar re-renders
  const lastCoordCountRef = useRef<number>(0)

  useEffect(() => {
    if (ativasGeocodingStatus?.isGeocoding) {
      ativasGeocodingPollRef.current = setInterval(async () => {
        const status = await checkAtivasGeocodingStatus()

        // Só recarregar ativas se o número de coordenadas mudou (evita flickering)
        if (status && status.comCoordenadas !== lastCoordCountRef.current) {
          lastCoordCountRef.current = status.comCoordenadas
          await loadAtivas()
        }

        if (status && !status.isGeocoding) {
          if (ativasGeocodingPollRef.current) {
            clearInterval(ativasGeocodingPollRef.current)
            ativasGeocodingPollRef.current = null
          }
          // Carregar dados finais e erros quando geocodificação terminar
          await loadAtivas()
          await loadGeocodingErrors()
        }
      }, 3000) // Polling a cada 3 segundos (era 1.5s)
    }

    return () => {
      if (ativasGeocodingPollRef.current) {
        clearInterval(ativasGeocodingPollRef.current)
      }
    }
  }, [ativasGeocodingStatus?.isGeocoding, checkAtivasGeocodingStatus, loadAtivas, loadGeocodingErrors])

  // Refs para controle de preferências (evita ciclos de re-render)
  const prefsInitializedRef = useRef(false)
  const lastSavedPrefsRef = useRef<{ showHeatmap: boolean; mapaUf: string | null } | null>(null)
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Carregar preferências do mapa do servidor (executa apenas uma vez)
  useEffect(() => {
    let mounted = true

    const loadPreferences = async () => {
      try {
        const response = await fetchApi(API_ENDPOINTS.MAPA_PREFERENCES)
        if (response.ok && mounted) {
          const data = await response.json()
          // Armazenar valores carregados como referência
          lastSavedPrefsRef.current = {
            showHeatmap: data.showHeatmap,
            mapaUf: data.mapaUf ?? null
          }
          // Atualizar estados
          setShowHeatmap(data.showHeatmap)
          // Estado em que o mapa abre. Quem trabalha uma região só (ex.: gestão
          // de GO) abre já recortada; o chip fica aceso, então a tela não
          // esconde que está filtrada.
          setUfSelecionada(data.mapaUf ?? null)
          // Marcar como inicializado DEPOIS de aplicar os valores
          setTimeout(() => {
            if (mounted) {
              prefsInitializedRef.current = true
              setPreferencesLoaded(true)
            }
          }, 100)
        }
      } catch (error) {
        console.error('Erro ao carregar preferências:', error)
        // Mesmo com erro, marcar como carregado para não bloquear
        if (mounted) {
          prefsInitializedRef.current = true
          setPreferencesLoaded(true)
        }
      }
    }

    loadPreferences()

    return () => {
      mounted = false
    }
  }, []) // Array vazio - executa apenas na montagem

  // Salvar preferências com debounce quando usuário mudar
  useEffect(() => {
    // Não salvar se ainda não carregou do servidor
    if (!prefsInitializedRef.current) {
      return
    }

    // Verificar se realmente mudou comparando com último valor salvo
    const currentPrefs = { showHeatmap, mapaUf: ufSelecionada }
    const lastPrefs = lastSavedPrefsRef.current

    if (lastPrefs &&
        lastPrefs.showHeatmap === currentPrefs.showHeatmap &&
        lastPrefs.mapaUf === currentPrefs.mapaUf) {
      return // Nada mudou, não salvar
    }

    // Limpar timeout anterior se existir
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current)
    }

    // Debounce: salvar após 500ms de inatividade
    saveTimeoutRef.current = setTimeout(async () => {
      try {
        await fetchApi(API_ENDPOINTS.MAPA_PREFERENCES, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(currentPrefs)
        })
        // Atualizar referência após salvar com sucesso
        lastSavedPrefsRef.current = currentPrefs
      } catch (error) {
        console.error('Erro ao salvar preferências:', error)
      }
    }, 500)

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current)
      }
    }
  }, [showHeatmap, ufSelecionada]) // Não inclui fetchApi - usa a ref

  // Modo Apresentação (Fullscreen)
  const togglePresentationMode = useCallback(() => {
    if (!mapContainerRef.current) return

    if (!document.fullscreenElement) {
      mapContainerRef.current.requestFullscreen().then(() => {
        setIsPresentationMode(true)
      }).catch((err) => {
        console.error('Erro ao entrar em fullscreen:', err)
      })
    } else {
      document.exitFullscreen().then(() => {
        setIsPresentationMode(false)
      }).catch((err) => {
        console.error('Erro ao sair de fullscreen:', err)
      })
    }
  }, [])

  // Listener para mudanças de fullscreen (ESC key, etc)
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsPresentationMode(!!document.fullscreenElement)
    }

    document.addEventListener('fullscreenchange', handleFullscreenChange)
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange)
    }
  }, [])

  // Upload de planilha de ativas - Etapa 1: Análise inteligente de colunas
  // ─── Oracle Sync ───
  const checkSyncStatus = useCallback(async () => {
    try {
      const response = await fetchApi(API_ENDPOINTS.MAPA_ATIVAS_SYNC_STATUS)
      if (response.ok) {
        const data = await response.json()
        setSyncStatus(data)
        return data
      }
    } catch { /* ignore */ }
    return null
  }, [fetchApi])

  const handleSyncNow = useCallback(async () => {
    try {
      await fetchApi(API_ENDPOINTS.MAPA_ATIVAS_SYNC_NOW, { method: 'POST' })
      setSyncStatus(prev => prev ? { ...prev, isSyncing: true } : prev)
      // Start fast polling
      if (syncPollRef.current) clearInterval(syncPollRef.current)
      syncPollRef.current = setInterval(async () => {
        const status = await checkSyncStatus()
        if (status && !status.isSyncing) {
          // Sync finished - reload data
          if (syncPollRef.current) clearInterval(syncPollRef.current)
          syncPollRef.current = setInterval(checkSyncStatus, 30000) // Back to slow polling
          await loadAtivas()
          await loadSemanas()
          await checkAtivasGeocodingStatus()
        }
      }, 3000)
    } catch (err) {
      setError('Erro ao iniciar sincronização')
    }
  }, [fetchApi, checkSyncStatus, loadAtivas, loadSemanas, checkAtivasGeocodingStatus])

  const formatSyncTimeAgo = useCallback((timestamp: string) => {
    const diff = Date.now() - new Date(timestamp).getTime()
    const mins = Math.floor(diff / 60000)
    if (mins < 1) return 'agora'
    if (mins < 60) return `há ${mins}min`
    const hours = Math.floor(mins / 60)
    if (hours < 24) return `há ${hours}h`
    return `há ${Math.floor(hours / 24)}d`
  }, [])

  // Limpar ativas
  const handleClearAtivas = useCallback(async () => {
    if (!confirm('Tem certeza que deseja remover todas as ativas da semana?')) return

    setIsLoading(true)
    setError(null)
    try {
      const response = await fetchApi(API_ENDPOINTS.MAPA_ATIVAS_LIMPAR, {
        method: 'DELETE'
      })
      if (!response.ok) throw new Error('Erro ao limpar ativas')
      setAtivas([])
      setGeocodingErrors([]) // Limpar erros anteriores
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao limpar')
    } finally {
      setIsLoading(false)
    }
  }, [fetchApi])

  // Reprocessar geocodificação de ativas
  const handleRetryGeocoding = useCallback(async () => {
    setError(null)
    try {
      const response = await fetchApi(API_ENDPOINTS.MAPA_ATIVAS_GEOCODING_RETRY, {
        method: 'POST'
      })
      const data = await response.json()
      if (!response.ok) throw new Error(data.error || 'Erro ao iniciar geocodificação')
      if (data.success) {
        checkAtivasGeocodingStatus()
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao reprocessar')
    }
  }, [fetchApi, checkAtivasGeocodingStatus])

  const handleDirectImport = useCallback(async (file: File) => {
    setIsUploading(true)
    setUploadProgress({ stage: 'upload', percent: 5, detail: 'Enviando arquivo...' })
    setError(null)

    try {
      const formData = new FormData()
      formData.append('file', file)

      const response = await fetchApi(API_ENDPOINTS.MAPA_UPLOAD_CLIENTES, {
        method: 'POST',
        body: formData
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Erro no upload')
      }

      setUploadProgress({ stage: 'concluido', percent: 100, detail: 'Upload concluído! Geocodificação em andamento...' })

      await loadClientes()
      await checkGeocodingStatus()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro no upload')
      setUploadProgress(null)
    } finally {
      setIsUploading(false)
      setPendingFile(null)
    }
  }, [fetchApi, loadClientes, checkGeocodingStatus])

  const handleFileSelect = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    setIsUploading(true)
    setError(null)
    setUploadProgress({ stage: 'upload', percent: 0, detail: 'Analisando arquivo...' })

    try {
      const formData = new FormData()
      formData.append('file', file)

      const response = await fetchApi(API_ENDPOINTS.MAPA_ANALYZE_FILE, {
        method: 'POST',
        body: formData
      })

      const result = await response.json()
      if (!response.ok) throw new Error(result.error || 'Erro na análise')

      setMappingResult(result)
      setPendingFile(file)
      setShowMappingModal(true)
      setUploadProgress(null)
    } catch (err) {
      // Fallback: se análise falhar, permitir importar direto
      const errorMsg = err instanceof Error ? err.message : 'Erro na análise'
      setError(`${errorMsg} — Importando diretamente...`)
      setUploadProgress(null)
      await handleDirectImport(file)
    } finally {
      setIsUploading(false)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }, [fetchApi, handleDirectImport])

  const handleConfirmImport = useCallback(async () => {
    if (!pendingFile) return
    setShowMappingModal(false)
    await handleDirectImport(pendingFile)
  }, [pendingFile, handleDirectImport])


  // ─── Recorte por Estado (UF) ───────────────────────────────────────────────
  // `ativasNaRegiao` é o ÚNICO ponto onde o filtro é aplicado: todo derivado de
  // LEITURA (pins, chips de semana, organização, overlay de inativos, resumo)
  // parte daqui, não de `ativas`. É o que impede o número da tela de falar da
  // praça inteira enquanto o mapa mostra um Estado só. As ferramentas de conserto
  // (Administração, geocodificação) seguem lendo `ativas` cru de propósito —
  // elas cuidam do dataset todo, não da região em foco.
  const ativasNaRegiao = useMemo(
    () => ufSelecionada ? ativas.filter(a => a.uf === ufSelecionada) : ativas,
    [ativas, ufSelecionada]
  )

  // UFs que a praça REALMENTE tem, com contagem — auto-montado a partir dos
  // dados (nada hardcodado): Planalto dá GO/DF/TO, a matriz daria só MS e aí o
  // bloco de chips nem aparece.
  // A régua é a MESMA dos chips de semana (contagemPorSemana): só quem vira pin
  // — tipo 1/2/3, com coordenada, na semana selecionada. Contando o Estado
  // inteiro o chip dizia "GO 2365" ao lado de 1129 pins na tela, e a soma dos
  // chips de semana não fechava com ele.
  const ufsDisponiveis = useMemo(() => {
    const m = new Map<string, number>()
    for (const a of ativas) {
      if (!a.uf || !a.latitude || !a.longitude) continue
      if (a.tipo_consultor !== '1' && a.tipo_consultor !== '2' && a.tipo_consultor !== '3') continue
      if (semanaSelecionada && a.semana !== semanaSelecionada) continue
      m.set(a.uf, (m.get(a.uf) || 0) + 1)
    }
    return [...m.entries()].map(([uf, total]) => ({ uf, total })).sort((a, b) => b.total - a.total)
  }, [ativas, semanaSelecionada])

  // Troca de Estado: tudo que aponta pra alguém do recorte anterior cai JUNTO,
  // no mesmo commit do clique. Fazer isso num useEffect [ufSelecionada] custava
  // um segundo commit — e nesse intervalo o OrgFocusEffect armava um flyToBounds
  // que o fitBounds seguinte matava no meio (o "tranco" de 18/08).
  //  · orgFocusGrupo: a líder isolada não está no Estado novo;
  //  · ativaDetail: o card ficava aberto com gente de outro Estado — e, enquanto
  //    aberto, o Painel Executivo fica colapsado ("o painel some");
  //  · mapFocus: senão a câmera reproduz o voo pro alvo antigo;
  //  · selectedSearchIndex: a lista de resultados encolhe e o índice velho
  //    estoura na seta ↑.
  const selecionarUf = useCallback((uf: string | null) => {
    setUfSelecionada(uf)
    setOrgFocusGrupo(null)
    setAtivaDetail(null)
    setMapFocus(null)
    setSelectedSearchIndex(0)
  }, [])

  // Distritos comerciais presentes no Estado em foco — é o que deixa o Painel
  // Executivo somar só a região (cada distrito pertence a uma UF só). Sai do
  // próprio dataset já carregado: sem tabela nem endpoint novo.
  const distritosDaRegiao = useMemo(() => {
    if (!ufSelecionada) return null
    const set = new Set<string>()
    for (const a of ativasNaRegiao) if (a.distrito_codigo) set.add(a.distrito_codigo)
    return [...set]
  }, [ufSelecionada, ativasNaRegiao])

  // Separar empresários, líderes e consultores (ativas) - OTIMIZADO: um único loop
  const { empresariosAtivas, lideresAtivas, consultorasAtivas } = useMemo(() => {
    const empresariosAtivas: AtivaData[] = []
    const lideresAtivas: AtivaData[] = []
    const consultorasAtivas: AtivaData[] = []

    // Um único loop em vez de múltiplos filter()
    for (const a of ativasNaRegiao) {
      // Filtrar por semana se necessário — EXCETO membros do grupo isolado:
      // ao clicar numa líder, a equipe inteira da vitrine aparece (a semana
      // vigente auto-selecionada escondia quem ativou em W-1/W-2/W-3 — o card
      // listava 6 consultoras e o mapa linkava só 3, bug real de 2026-07-08).
      const noGrupoFocado = orgFocusGrupo !== null && a.grupo_codigo === orgFocusGrupo
      if (semanaSelecionada && a.semana !== semanaSelecionada && !noGrupoFocado) continue

      // Triagem: mostrar só localização imprecisa (centróide do município). Passa
      // a fonte junto — sem ela, bairro/RA cairia na triagem como se fosse
      // centróide, e a lista de "para revisar" viria com 1.937 falsos.
      if (onlyLowConfidence && !confidenceMeta(a.geo_confidence as GeoConfidence, a.geo_source).alert) continue

      if (a.latitude && a.longitude) {
        if (a.tipo_consultor === '1') empresariosAtivas.push(a)
        else if (a.tipo_consultor === '2') lideresAtivas.push(a)
        else if (a.tipo_consultor === '3') consultorasAtivas.push(a)
      }
    }

    return { empresariosAtivas, lideresAtivas, consultorasAtivas }
  }, [ativasNaRegiao, semanaSelecionada, onlyLowConfidence, orgFocusGrupo])

  // Contagem RENDERÁVEL por semana (tipo 1/2/3 + com coordenada) — bate com o
  // que o mapa desenha. A contagem do backend (com_coordenadas) incluía tipo 0
  // (Ger. Distribuição, ex.: Juliane, que NÃO é plotada), fazendo o chip dizer
  // "1" e o mapa mostrar nada (caso W28, 2026-07-14). Alimenta o número do chip
  // e a regra de esconder semanas vazias passadas.
  const contagemPorSemana = useMemo(() => {
    const m = new Map<string, number>()
    for (const a of ativasNaRegiao) {
      if (!a.latitude || !a.longitude || !a.semana) continue
      if (a.tipo_consultor !== '1' && a.tipo_consultor !== '2' && a.tipo_consultor !== '3') continue
      m.set(a.semana, (m.get(a.semana) || 0) + 1)
    }
    return m
  }, [ativasNaRegiao])

  // Auto-selecionar a semana MAIS POPULOSA (2026-07-14): antes abria na "última
  // com dados", que podia ter 1 ativa → mapa parecia vazio.
  //
  // Desde 18/08 a conta é feita sobre `contagemPorSemana`, que JÁ está recortada
  // pelo Estado — e reavalia quando o recorte muda. Antes ela era global e
  // travada por ref: a semana corrente nasce com venda só numa UF (hoje W33 tem
  // GO 71 / DF 0 / TO 0), então clicar em DF caía numa combinação sem nenhum pin
  // e a tela ficava vazia.
  //
  // Só corrige quando a semana em foco não tem pin no recorte — escolha manual
  // de semana com dado é respeitada. `semanaSelecionada` NÃO entra nas deps (é
  // lida pelo updater funcional): entraria em ciclo.
  useEffect(() => {
    if (contagemPorSemana.size === 0) return
    setSemanaSelecionada(prev => {
      if (prev && (contagemPorSemana.get(prev) || 0) > 0) return prev
      const populosa = [...contagemPorSemana.entries()].sort((a, b) => b[1] - a[1])[0]
      return populosa ? populosa[0] : prev
    })
  }, [contagemPorSemana])

  // Semanas VISÍVEIS nos chips: as com ativas + as vazias que são atual/futuras
  // (>= última com dado). Esconde as vazias JÁ PASSADAS (só ruído — pedido
  // 2026-07-14: "quem está com 0 nem adianta exibir se for passada"). Regra
  // adaptativa (sem hardcodar semana) — hoje resulta em "da W26 pra frente".
  const semanasVisiveis = useMemo(() => {
    const ultimaComDado = semanas
      .filter(s => (contagemPorSemana.get(s.semana_codigo) || 0) > 0)
      .map(s => s.semana_codigo)
      .sort()
      .pop() || ''
    return semanas.filter(s => (contagemPorSemana.get(s.semana_codigo) || 0) > 0 || s.semana_codigo >= ultimaComDado)
  }, [semanas, contagemPorSemana])

  // Blocos de chips por vitrine (período comercial de 4 semanas). Espelha o
  // calendário oficial do DRACO (SEMANAS.SPERIODO = ano + teto(semana/4)) sem
  // consultar o Oracle — o agrupamento é só apresentação. Vitrine mais nova
  // primeiro; semana sem formato AAAA-WSS cai no bloco "Outras" (numero 0).
  const semanasPorVitrine = useMemo(() => {
    const grupos = new Map<string, { ano: number; numero: number; ini: number; fim: number; itens: typeof semanasVisiveis }>()
    for (const s of semanasVisiveis) {
      const m = s.semana_codigo.match(/^(\d{4})-W(\d+)$/)
      const ano = m ? Number(m[1]) : 0
      const numero = m ? Math.ceil(Number(m[2]) / 4) : 0
      const key = `${ano}-${numero}`
      if (!grupos.has(key)) grupos.set(key, { ano, numero, ini: numero * 4 - 3, fim: numero * 4, itens: [] })
      grupos.get(key)!.itens.push(s)
    }
    return [...grupos.values()].sort((a, b) => b.ano - a.ano || b.numero - a.numero)
  }, [semanasVisiveis])

  // Busca — filtra as ativas visíveis (nome, grupo, distrito ou telefone com
  // 4+ dígitos, evita DDD sozinho casando com quase todo mundo). Só filtra;
  // não navega/dá zoom sozinho a cada tecla (era isso que deixava a câmera
  // "voando" sem parar, mesmo bug já corrigido no painel Rede — ver memória
  // feedback-search-bar-mapa-nao-auto-zoom). Zoom só no clique de um
  // resultado ou nas setas ↑/↓ (handleSearchKeyDown, abaixo).
  const pessoasParaBusca = useMemo(
    () => [...empresariosAtivas, ...lideresAtivas, ...consultorasAtivas],
    [empresariosAtivas, lideresAtivas, consultorasAtivas]
  )
  const matchedPessoas = useMemo(() => {
    if (!searchTerm || searchTerm.length < 2) return []
    const termNormalizado = normalizeText(searchTerm)
    const termDigits = searchTerm.replace(/\D/g, '')
    return pessoasParaBusca.filter(p => {
      if (!p.latitude || !p.longitude) return false
      const textHit = normalizeText(p.nome || '').includes(termNormalizado) ||
        normalizeText(p.grupo_nome || '').includes(termNormalizado) ||
        normalizeText(p.distrito_nome || '').includes(termNormalizado)
      const phoneHit = termDigits.length >= 4 && !!p.telefone && p.telefone.replace(/\D/g, '').includes(termDigits)
      return textHit || phoneHit
    })
  }, [searchTerm, pessoasParaBusca])

  const navigateToSearchResult = useCallback((pessoa: AtivaData, index: number, zoom = 16) => {
    if (pessoa.latitude == null || pessoa.longitude == null) return
    setSelectedSearchIndex(index)
    setAtivaDetail(pessoa) // abre o painel de detalhes (padrão Rede)
    // Resultado líder também isola o grupo (mesmo efeito do clique no pin —
    // senão o pin dela vira um toggle dessincronizado do painel já aberto);
    // quem enquadra nesse caso é o OrgFocusEffect.
    if (pessoa.tipo_consultor === '2' && pessoa.grupo_codigo && showOrgLinksRef.current) {
      setOrgFocusGrupo(pessoa.grupo_codigo)
    } else {
      setMapFocus({ lat: pessoa.latitude, lng: pessoa.longitude, zoom })
    }
  }, [])

  const handleSearchChange = useCallback((value: string) => {
    setSearchTerm(value)
    setSelectedSearchIndex(0)
  }, [])

  const handleSearchClear = useCallback(() => {
    setSearchTerm('')
  }, [])

  const handleSearchKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      setSearchTerm('')
      setAtivaDetail(null)
      setMapFocus({ ...DEFAULT_MAP_CENTER })
      return
    }
    if (matchedPessoas.length === 0) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      const newIndex = (selectedSearchIndex + 1) % matchedPessoas.length
      navigateToSearchResult(matchedPessoas[newIndex], newIndex)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      const newIndex = selectedSearchIndex === 0 ? matchedPessoas.length - 1 : selectedSearchIndex - 1
      navigateToSearchResult(matchedPessoas[newIndex], newIndex)
    }
  }, [matchedPessoas, selectedSearchIndex, navigateToSearchResult])

  // Clique num membro listado no painel (consultora da equipe, líder da
  // estrutura da empresária) — navega o painel/mapa pra ele. Alvo líder com a
  // camada Organização ligada também isola o grupo (mesmo efeito do clique no
  // pin); aí quem enquadra é o OrgFocusEffect, sem flyTo concorrente.
  const focusEquipeMember = useCallback((codigo: string) => {
    // Busca no recorte em foco: com um Estado filtrado, membro de outra UF não
    // tem pin na tela — navegar pra ele deixaria o painel apontando pro vazio.
    const alvo = ativasNaRegiao.find(a => a.codigo === codigo)
    if (!alvo) return
    setAtivaDetail(alvo)
    if (alvo.tipo_consultor === '2' && alvo.grupo_codigo && showOrgLinksRef.current) {
      setOrgFocusGrupo(alvo.grupo_codigo)
    } else if (alvo.latitude != null && alvo.longitude != null) {
      setMapFocus({ lat: alvo.latitude, lng: alvo.longitude, zoom: 15 })
    }
  }, [ativasNaRegiao])

  // Número de ativas sem coordenadas (filtrado pela semana selecionada)
  const ativasSemCoordenadas = useMemo(() => {
    const filtered = semanaSelecionada
      ? ativas.filter(a => a.semana === semanaSelecionada)
      : ativas
    return filtered.filter(a => !a.latitude && a.cep).length
  }, [ativas, semanaSelecionada])

  // Set de códigos das ativas da semana (para cross-reference com clientes)
  // Usado para determinar se um cliente está ativo (colorido) ou inativo (cinza)
  const codigosAtivos = useMemo(() => {
    const codigos = new Set<string>()
    ativas.forEach(a => {
      if (a.codigo) {
        codigos.add(a.codigo.toUpperCase().trim())
      }
    })
    return codigos
  }, [ativas])

  // Clientes inativos (com coordenadas, que NÃO estão na lista de ativas).
  // NÃO é recortado por Estado, e isso é decisão consciente: o cadastro inativo
  // não tem `endereco_uf` (o backfill do sync só passa em quem está ativo), e
  // recortar por nome de cidade apagava a camada inteira — os poucos inativos
  // com coordenada estão justamente em cidade SEM ativa, que é o buraco que a
  // camada existe pra mostrar. Além disso o recorte remontava o cluster inteiro
  // a cada clique de chip (redesenho progressivo de ~10s, cidade por cidade).
  // `codigosAtivos` também fica em `ativas` cru: ele responde "quem é ativa", e
  // recortá-lo faria a ativa de outro Estado reaparecer aqui pintada de inativa.
  // Pontos do mapa de calor: respeita os toggles de camada (se a pessoa
  // desligou "Consultora", o calor dela também sai) e usa peso 1 por pessoa.
  const heatPoints = useMemo<[number, number][]>(() => {
    const out: [number, number][] = []
    const push = (lista: AtivaData[]) => {
      for (const a of lista) if (a.latitude != null && a.longitude != null) out.push([a.latitude, a.longitude])
    }
    if (showConsultoras) push(consultorasAtivas)
    if (showLideres) push(lideresAtivas)
    if (showEmpresarios) push(empresariosAtivas)
    return out
  }, [showConsultoras, showLideres, showEmpresarios, consultorasAtivas, lideresAtivas, empresariosAtivas])

  // Quantas ativas estão numa coordenada FABRICADA (centro do município +
  // ruído). Alimenta a linha de legenda do pin tracejado. `alert` é o mesmo
  // critério que o painel de detalhe já usa — bairro não conta, só nível de
  // cidade, que é o que engana.
  const ativasImprecisas = useMemo(
    () => ativas.filter(a => confidenceMeta(a.geo_confidence as GeoConfidence, a.geo_source).alert).length,
    [ativas]
  )

  const clientesInativosOverlay = useMemo(() => {
    if (clientes.length === 0 || ativas.length === 0) return []
    return clientes.filter(c =>
      c.latitude && c.longitude &&
      !codigosAtivos.has(c.codigo.toUpperCase().trim())
    )
  }, [clientes, ativas.length, codigosAtivos])


  // Centro do mapa (média das coordenadas das ativas)
  const cidadeCenter = useMemo((): [number, number] => {
    const dados = [...lideresAtivas, ...consultorasAtivas]
    if (dados.length === 0) {
      return [DEFAULT_MAP_CENTER.lat, DEFAULT_MAP_CENTER.lng] // centro da praca (config)
    }
    const sumLat = dados.reduce((sum, c) => sum + c.latitude!, 0)
    const sumLng = dados.reduce((sum, c) => sum + c.longitude!, 0)
    return [sumLat / dados.length, sumLng / dados.length]
  }, [lideresAtivas, consultorasAtivas])

  // Exportar relatório de cruzamento em CSV


  const handleReset = useCallback(() => {
    // Incrementar a key força o ZoomableGroup a ser recriado com valores iniciais
    setMapResetKey(prev => prev + 1)
  }, [])

  // Cobertura por município — carrega na 1ª vez que a camada é ativada.
  // /api/leader-coverage já existe (painel Rede) — só reaproveitando aqui.
  const loadMunicipioCoverage = useCallback(async () => {
    try {
      const [coverageRes, geoRes] = await Promise.all([
        fetchApi('/api/leader-coverage'),
        fetchApi(mapCfg?.geojsonUrl || '/api/mapa/municipios-geojson'),
      ])
      const coverageJson = await coverageRes.json()
      const geoJson = await geoRes.json()
      if (coverageJson?.success) setMunicipioCoverageData(coverageJson.cidades)
      setMunicipioGeoFeatures(geoJson.features || [])
    } catch { /* silent — camada opcional */ }
    finally { setMunicipioCoverageLoaded(true) }
  }, [fetchApi, mapCfg])
  useEffect(() => {
    if (showMunicipioCoverage && !municipioCoverageLoaded) loadMunicipioCoverage()
  }, [showMunicipioCoverage, municipioCoverageLoaded, loadMunicipioCoverage])

  // Organização real — mapa grupo_codigo → líder (dona do grupo), derivado
  // do PRÓPRIO dataset de ativas já carregado (sem fetch novo). Depois, uma
  // linha permanente por consultora cujo grupo resolve pra uma líder ativa.
  const liderPorGrupo = useMemo(() => {
    const m = new Map<string, AtivaData>()
    for (const a of ativasNaRegiao) {
      if (a.tipo_consultor === '2' && a.grupo_codigo && a.latitude != null && a.longitude != null) {
        m.set(a.grupo_codigo, a)
      }
    }
    return m
  }, [ativasNaRegiao])
  const orgLinks = useMemo(() => {
    if (!showOrgLinks) return []
    // Mesmo bypass do filtro de semana usado nos pins: o grupo isolado linka a
    // equipe inteira da vitrine, não só quem ativou na semana selecionada.
    const filtered = ativasNaRegiao.filter(a =>
      !semanaSelecionada || a.semana === semanaSelecionada ||
      (orgFocusGrupo !== null && a.grupo_codigo === orgFocusGrupo))
    const links: Array<{ key: string; from: [number, number]; to: [number, number]; consultoraNome: string; liderNome: string; grupoCode: string; consultoraValor: number }> = []
    for (const a of filtered) {
      if (a.tipo_consultor !== '3' || !a.grupo_codigo || a.latitude == null || a.longitude == null) continue
      const lider = liderPorGrupo.get(a.grupo_codigo)
      if (!lider || lider.latitude == null || lider.longitude == null) continue
      links.push({
        key: a.codigo,
        from: [a.latitude, a.longitude], to: [lider.latitude, lider.longitude],
        consultoraNome: a.nome, liderNome: lider.nome, grupoCode: a.grupo_codigo,
        consultoraValor: a.valor_venda_vitrine || 0,
      })
    }
    return links
  }, [showOrgLinks, ativasNaRegiao, semanaSelecionada, liderPorGrupo, orgFocusGrupo])

  // Valor da vitrine somado por equipe (consultoras resolvidas de cada líder)
  // — complementa a camada Organização com o dado que já vem sincronizado do
  // Oracle (valor_venda_vitrine), sem cálculo novo. Consumido no popup da líder.
  const equipeValorPorGrupo = useMemo(() => {
    const m = new Map<string, { valor: number; count: number }>()
    for (const link of orgLinks) {
      const prev = m.get(link.grupoCode) || { valor: 0, count: 0 }
      m.set(link.grupoCode, { valor: prev.valor + link.consultoraValor, count: prev.count + 1 })
    }
    return m
  }, [orgLinks])

  // Isolamento por líder — zoom/esmaecimento, mesmo padrão do painel Rede.
  const isOrgIsolating = showOrgLinks && orgFocusGrupo !== null
  const orgFocusPositions = useMemo((): [number, number][] | null => {
    if (!orgFocusGrupo) return null
    const lider = liderPorGrupo.get(orgFocusGrupo)
    if (!lider || lider.latitude == null || lider.longitude == null) return null
    const pts: [number, number][] = [[lider.latitude, lider.longitude]]
    for (const link of orgLinks) {
      if (link.grupoCode === orgFocusGrupo) pts.push(link.from)
    }
    return pts
  }, [orgFocusGrupo, liderPorGrupo, orgLinks])
  useEffect(() => { if (!showOrgLinks) setOrgFocusGrupo(null) }, [showOrgLinks])
  useEffect(() => { setOrgFocusGrupo(null) }, [semanaSelecionada])

  // Casa a cobertura por município (Oracle, via /api/leader-coverage) com o
  // contorno do IBGE — mesmo casamento por nome normalizado do painel Rede.
  const municipioGeoMatched = useMemo(() => {
    if (!municipioGeoFeatures || !municipioCoverageData) return null
    // Com um Estado filtrado, só os municípios dele são pintados — o GeoJSON já
    // traz a UF por feature (geo-praca.cjs), e /api/leader-coverage é global.
    const fonte = ufSelecionada
      ? municipioGeoFeatures.filter((f: any) => String(f.properties?.uf || '').toUpperCase() === ufSelecionada)
      : municipioGeoFeatures
    const byName = new Map(fonte.map((f: any) => [normCidade(f.properties.name), f]))
    const features: any[] = []
    for (const c of municipioCoverageData) {
      const feat = byName.get(normCidade(c.cidade))
      if (feat) features.push({ ...feat, properties: { ...feat.properties, coverage: c } })
    }
    return { type: 'FeatureCollection', features } as any
  }, [municipioGeoFeatures, municipioCoverageData, ufSelecionada])
  // Cobertura esmaece junto durante o isolamento (o verde saturado competia
  // com o grupo focado). GeoJSON só re-estiliza no remount — a key do
  // componente inclui o estado (ver memória feedback-react-leaflet-props-update-quirk).
  // "Sem líder" é AUSÊNCIA. Sobre o tile claro o cinza-400 lia como área
  // apagada; sobre o Dark Matter ele vira a coisa mais clara da tela e a
  // ausência passa a gritar mais que a cobertura — a ênfase do mapa inverte.
  // No escuro desce pro cinza-600: continua neutro sem roubar o olho do verde.
  const cinzaSemLider = isDark ? '#4b5563' : '#9ca3af'
  const municipioStyle = (feature?: any) => {
    const c = feature?.properties?.coverage
    if (isOrgIsolating) {
      return {
        color: '#fff', weight: 1, opacity: 0.15,
        fillColor: c?.gap ? cinzaSemLider : '#22c55e',
        fillOpacity: c?.gap ? 0.1 : 0.06,
      }
    }
    return {
      color: '#fff', weight: 1, opacity: 0.7,
      fillColor: c?.gap ? cinzaSemLider : '#22c55e',
      fillOpacity: c?.gap ? 0.5 : 0.35,
    }
  }
  // Popup do município é manual (não bindPopup): com isolamento ativo, painel
  // de detalhes aberto ou busca em andamento, o clique no polígono é "clique
  // no fundo" — deve só fechar/deisolar/limpar, sem abrir popup por cima
  // (mesmo padrão do painel Rede, ver LeaderCoverageView ~478).
  const uiBusyRef = useRef(false)
  uiBusyRef.current = isOrgIsolating || ativaDetail !== null || searchTerm.length >= 2
  const municipioOnEachFeature = (feature: any, layer: L.Layer) => {
    const c = feature.properties.coverage
    if (!c) return
    layer.on('click', (e: any) => {
      if (uiBusyRef.current) return
      L.popup()
        .setLatLng(e.latlng)
        .setContent(`<div style="font-size:12px;line-height:1.5">
          <div style="font-weight:600">${escHtmlMapa(c.cidade)}</div>
          <div>${c.consultoras} consultora${c.consultoras === 1 ? '' : 's'}</div>
          <div>${c.lideres} líder${c.lideres === 1 ? '' : 'es'}</div>
          ${c.gap
            ? '<div style="color:#6b7280;font-weight:600;margin-top:4px">Sem líder local — área a desbloquear</div>'
            : `<div style="color:#6b7280">${c.consultorasPorLider} consultoras/líder</div>`}
        </div>`)
        .openOn(e.target._map)
    })
  }

  // ─── Arrastar pin pra corrigir posição ───
  const handleMarkerDragEnd = useCallback(async (ativa: AtivaData, lat: number, lng: number) => {
    setDragConfirm({ ativa, lat, lng, address: '', loading: true })
    try {
      const res = await fetchApi(`/api/mapa/reverse-geocode?lat=${lat}&lng=${lng}`)
      const data = await res.json()
      const a = data?.address || {}
      const parts = [
        [a.logradouro, a.numero].filter(Boolean).join(', '),
        a.bairro, a.cidade, a.uf
      ].filter(Boolean)
      const address = data?.success ? (parts.join(' — ') || data.display_name || 'Endereço não identificado') : 'Endereço não identificado'
      setDragConfirm(prev => prev && prev.ativa.codigo === ativa.codigo ? { ...prev, address, loading: false } : prev)
    } catch {
      setDragConfirm(prev => prev && prev.ativa.codigo === ativa.codigo ? { ...prev, address: 'Falha ao resolver endereço', loading: false } : prev)
    }
  }, [fetchApi])

  const confirmDragMove = useCallback(async () => {
    if (!dragConfirm) return
    const { ativa, lat, lng } = dragConfirm
    try {
      const res = await fetchApi(`/api/mapa/cadastro/${ativa.codigo}/coords`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ latitude: lat, longitude: lng }),
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err.error || 'Erro ao salvar posição')
      }
      // Atualiza estado local + marca como manual (alta confiança), sem refetch
      setAtivas(prev => prev.map(a => a.codigo === ativa.codigo
        ? { ...a, latitude: lat, longitude: lng, geo_confidence: 'manual', geo_source: 'manual_pin' }
        : a))
      setDragConfirm(null)
      setMarkerEpoch(e => e + 1)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar posição')
      setDragConfirm(null)
      setMarkerEpoch(e => e + 1) // snap-back se falhar
    }
  }, [dragConfirm, fetchApi])

  const cancelDragMove = useCallback(() => {
    setDragConfirm(null)
    setMarkerEpoch(e => e + 1) // remonta marcadores → pin volta à posição original
  }, [])

  return (
    <div
      ref={mapContainerRef}
      className={`relative flex flex-col h-full w-full ${className} ${isPresentationMode ? 'presentation-mode' : ''}`}
    >

      {/* Container principal com mapa e painel (full-bleed — sem header; controles flutuam) */}
      <div className="neu-forms relative flex-1 min-h-0 flex bg-gradient-to-br from-blue-500/5 via-transparent to-cyan-500/5">
        {/* Container do Mapa */}
        <div className="flex-1 min-h-0 flex">
          {/* Mapa */}
          <div className="flex-1 min-h-0 p-2 sm:p-4">
            <div className="relative w-full h-full rounded-2xl border border-white/10 overflow-hidden shadow-lg">

            {/* ─── Barra única flutuante (padrão do painel Rede, LeaderCoverageView.tsx):
                 busca, semanas, resumo, legenda de camadas e administração — tudo numa
                 caixa só, sem popover "Camadas" nem drawer ⚙ separados. Ver memória
                 project-crm-twms-mapa-ativas-melhorias-rede pro histórico da unificação. ─── */}
            {/* Painéis SEMPRE visíveis, inclusive em apresentação (2026-07-14): os
                mesmos controles + o painel executivo vão junto pro fullscreen. */}
            <>
                {/* `bottom-3` é o que dá ALTURA ao wrapper. Sem ele o
                    max-h-[calc(100%-24px)] do painel resolve contra altura
                    indefinida (= sem teto), o overflow-y-auto nunca liga e o
                    rodapé do painel (camadas, Administração) some cortado pelo
                    overflow-hidden do card — piorou em 18/08 com o bloco Estado.
                    pointer-events: a faixa vazia do wrapper não pode roubar
                    clique do mapa. */}
                <div className="absolute top-3 left-3 bottom-3 z-[1000] flex items-start gap-2 pointer-events-none">
                  {/* Recolhido: vira uma pílula clicável (padrão do ExecutivoPanel). */}
                  {!painelAberto && (
                    <button onClick={() => setPainelAberto(true)}
                      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl neu-float-2 text-foreground hover:bg-foreground/5 transition-colors text-xs font-semibold pointer-events-auto"
                      title="Abrir controles do mapa (busca, semanas, camadas)">
                      <FiFilter className="w-4 h-4 text-amber-500" /> Controles
                      <FiChevronRight className="w-3 h-3" />
                    </button>
                  )}
                  {painelAberto && (<>
                  <div className="neu-float rounded-lg px-3 py-2 text-[11px] space-y-1.5 shadow-lg w-[min(16rem,calc(100vw-1.5rem))] max-h-full overflow-y-auto pointer-events-auto">
                    {/* Busca — estado vive no componente pai (fora do Leaflet); zoom só
                        no clique de um resultado ou nas setas ↑/↓, nunca a cada tecla.
                        O X ao lado recolhe o painel inteiro (pedido da diretoria). */}
                    <div className="flex items-center gap-1.5">
                      <div className="relative flex-1">
                        <FiSearch className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground pointer-events-none" />
                        <input
                          type="text"
                          value={searchTerm}
                          onChange={(e) => handleSearchChange(e.target.value)}
                          onKeyDown={handleSearchKeyDown}
                          placeholder="Buscar nome, grupo, distrito, telefone…"
                          className="w-full pl-6 pr-6 py-1 bg-background border border-border rounded focus:outline-none focus:ring-1 focus:ring-amber-500"
                        />
                        {searchTerm && (
                          <button onClick={handleSearchClear} title="Limpar busca"
                            className="absolute right-1.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                            <FiX className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                      <button onClick={() => setPainelAberto(false)} title="Recolher painel (libera a visão do mapa)"
                        className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted/60 shrink-0">
                        <FiX className="w-3.5 h-3.5" />
                      </button>
                    </div>

                    {/* Estado (UF) — recorte geográfico mais grosso, acima das
                        semanas de propósito: é o primeiro corte que a diretoria
                        faz ("me mostra só o DF"). Só aparece onde a praça tem
                        mais de um Estado — em praça de UF única (matriz/MS) um
                        chip solitário seria ruído. Chips no mesmo estilo dos de
                        semana; o número é quantas ativas plotáveis o Estado tem. */}
                    {ufsDisponiveis.length > 1 && (
                      <div className="space-y-1 pt-0.5">
                        <div className="text-[9px] uppercase tracking-wide text-muted-foreground/70 font-semibold">
                          Estado
                        </div>
                        <div className="flex flex-wrap gap-1">
                          <button onClick={() => selecionarUf(null)} title="Mostrar a praça inteira"
                            className={`px-2 py-1 rounded text-[11px] font-medium transition-colors ${
                              !ufSelecionada ? 'bg-amber-500 text-white' : 'bg-secondary/60 text-muted-foreground hover:bg-secondary'}`}>
                            Todos
                          </button>
                          {ufsDisponiveis.map(({ uf, total }) => {
                            const selected = ufSelecionada === uf
                            return (
                              <button key={uf} onClick={() => selecionarUf(selected ? null : uf)}
                                title={`Mostrar só ${uf} — ${total} ativa${total === 1 ? '' : 's'} no mapa${semanaSelecionada ? ' nesta semana' : ''}.`}
                                className={`inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium transition-colors ${
                                  selected ? 'bg-amber-500 text-white' : 'bg-secondary/60 text-muted-foreground hover:bg-secondary'}`}>
                                <span>{uf}</span>
                                <span className={`tabular-nums text-[10px] ${selected ? 'text-white/80' : 'text-muted-foreground/55'}`}>{total}</span>
                              </button>
                            )
                          })}
                        </div>
                      </div>
                    )}

                    {/* Semanas — chips sempre visíveis (troca instantânea, filtro
                        local), agrupados em BLOCOS POR VITRINE (período comercial
                        de 4 semanas — pedido da diretoria 2026-07-14). Renomear/
                        excluir vivem só no chip selecionado (não faz sentido editar
                        uma semana sem estar olhando pra ela) — evita duplicar a
                        mesma lista noutro lugar (Administração). */}
                    <div className="space-y-1 pt-0.5">
                      {/* O número do chip é a PARTIÇÃO das ativas por semana da
                          ÚLTIMA venda (o sync grava MAX(PESEMANA) do DRACO em
                          `semana_ativa`, server.cjs:32185), não "quantas estavam
                          ativas naquela semana". São coisas diferentes: quem
                          comprou na W31 e voltou a comprar na W32 conta só na
                          W32. Por isso a semana corrente parece "cheia" e as
                          anteriores parecem encolhidas — e por isso a soma dos
                          chips fecha com o total da vitrine do Painel Executivo,
                          em vez de bater com "ativas na semana". Sem este
                          rótulo o número lido isolado engana (auditado 17/08). */}
                      <div className="text-[9px] uppercase tracking-wide text-muted-foreground/70 font-semibold">
                        Por semana da última venda
                      </div>
                      <div className="flex flex-wrap gap-1">
                        <button onClick={() => setSemanaSelecionada(null)} title="Mostrar todas as semanas"
                          className={`px-2 py-1 rounded text-[11px] font-medium transition-colors ${
                            !semanaSelecionada ? 'bg-amber-500 text-white' : 'bg-secondary/60 text-muted-foreground hover:bg-secondary'}`}>
                          Todas
                        </button>
                      </div>
                      {semanasPorVitrine.map(g => (
                        <div key={`${g.ano}-${g.numero}`} className="rounded-lg border border-border/60 bg-muted/20 px-1.5 py-1">
                          <div className="text-[9px] uppercase tracking-wide text-muted-foreground font-semibold mb-1">
                            {g.numero > 0 ? `Vitrine ${g.numero} · S${String(g.ini).padStart(2, '0')}–S${String(g.fim).padStart(2, '0')}` : 'Outras'}
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {g.itens.map(s => {
                        const selected = semanaSelecionada === s.semana_codigo
                        const contagem = contagemPorSemana.get(s.semana_codigo) || 0
                        if (editingSemana === s.semana_codigo) {
                          return (
                            <div key={s.semana_codigo} className="flex items-center gap-1">
                              <input
                                type="text" value={editingNome} onChange={(e) => setEditingNome(e.target.value)}
                                className="w-24 px-1.5 py-1 text-[11px] bg-background border border-border rounded" autoFocus
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') handleRenameSemana(s.semana_codigo, editingNome)
                                  if (e.key === 'Escape') { setEditingSemana(null); setEditingNome('') }
                                }}
                              />
                              <button onClick={() => handleRenameSemana(s.semana_codigo, editingNome)} className="text-emerald-500 hover:text-emerald-400"><FiCheck className="w-3 h-3" /></button>
                              <button onClick={() => { setEditingSemana(null); setEditingNome('') }} className="text-muted-foreground hover:text-foreground"><FiX className="w-3 h-3" /></button>
                            </div>
                          )
                        }
                        // Chip = toggle puro, LARGURA FIXA por seleção (renomear/
                        // excluir saíram daqui pro slot fixo abaixo). Antes os ícones
                        // inline no chip selecionado alargavam ele e reordenavam a
                        // fileira a cada clique (2026-07-14). Contagem de ativas no
                        // mapa embutida (com_coordenadas): semanas vazias ficam
                        // esmaecidas — dá pra ver de cara onde tem dado.
                        const vazia = contagem === 0
                        return (
                          <button key={s.semana_codigo} onClick={() => setSemanaSelecionada(s.semana_codigo)}
                            title={`${s.nome} · ${contagem} consultora${contagem === 1 ? '' : 's'} no mapa cuja última venda foi nesta semana.\nNão é o total de ativas na semana: quem comprou depois conta na semana mais recente.`}
                            className={`inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium transition-colors ${
                              selected ? 'bg-amber-500 text-white' : vazia ? 'bg-secondary/30 text-muted-foreground/45 hover:bg-secondary/50' : 'bg-secondary/60 text-muted-foreground hover:bg-secondary'}`}>
                            <span className="truncate max-w-[110px] align-middle">{s.nome}</span>
                            <span className={`tabular-nums text-[10px] ${selected ? 'text-white/80' : 'text-muted-foreground/55'}`}>{contagem}</span>
                          </button>
                        )
                            })}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Ações da semana selecionada — slot FIXO (não mexe na fileira
                        de chips). Só aparece com uma semana específica selecionada. */}
                    {semanaSelecionada && (
                      <div className="flex items-center gap-2 pt-1 text-[10px] text-muted-foreground">
                        <span className="opacity-70">Semana selecionada:</span>
                        <button onClick={() => { const s = semanas.find(x => x.semana_codigo === semanaSelecionada); if (s) { setEditingSemana(s.semana_codigo); setEditingNome(s.nome) } }}
                          title="Renomear semana" className="inline-flex items-center gap-1 hover:text-foreground transition-colors">
                          <FiEdit2 className="w-3 h-3" /> Renomear
                        </button>
                        <button onClick={() => handleDeleteSemana(semanaSelecionada)}
                          title="Excluir semana" className="inline-flex items-center gap-1 hover:text-red-500 transition-colors">
                          <FiTrash2 className="w-3 h-3" /> Excluir
                        </button>
                      </div>
                    )}

                    {/* Resumo compacto — contagem por tipo + status de sync Oracle */}
                    <div className="flex items-center gap-2.5 text-foreground border-t border-border pt-1.5">
                      <span className="flex items-center gap-1" title="Empresários (C1)"><span className="w-2 h-2 rounded-full bg-[#f59e0b]" />{empresariosAtivas.length}</span>
                      <span className="flex items-center gap-1" title="Líderes (C2)"><span className="w-2 h-2 rounded-full bg-[#a855f7]" />{lideresAtivas.length}</span>
                      <span className="flex items-center gap-1" title="Consultoras (C3)"><span className="w-2 h-2 rounded-full bg-[#0ea5e9]" />{consultorasAtivas.length}</span>
                      <span className="flex items-center gap-1 ml-0.5 text-muted-foreground" title={syncStatus?.isSyncing ? 'Sincronizando Oracle…' : syncStatus?.lastResult?.success === false ? 'Erro na sincronização' : syncStatus?.lastResult?.success ? 'Oracle sincronizado' : 'Aguardando sync'}>
                        <span className={`w-2 h-2 rounded-full ${syncStatus?.isSyncing ? 'bg-amber-500 animate-pulse' : syncStatus?.lastResult?.success === false ? 'bg-red-500' : syncStatus?.lastResult?.success ? 'bg-emerald-500' : 'bg-gray-400'}`} />
                        sync
                      </span>
                    </div>

                    {/* Camadas — legenda-toggle sempre visível, sem popover (mesmo
                        padrão da legenda-toggle da rede em LeaderCoverageView.tsx) */}
                    <div className="border-t border-border pt-1.5 space-y-0.5">
                      <button type="button" onClick={() => setShowHeatmap(p => !p)} className="w-full flex items-center gap-2 -mx-1 px-1 py-0.5 rounded hover:bg-muted/50 text-left" title="Densidade de ativas por área. Peso 1 por pessoa; o raio acompanha o zoom.">
                        <span className="text-foreground flex-1 truncate">Mapa de calor</span>
                        <span className={`relative w-7 h-4 rounded-full neu-switch flex-shrink-0 transition-colors ${showHeatmap ? 'bg-primary' : 'bg-muted'}`}><span className={`absolute top-0.5 left-0.5 w-3 h-3 bg-white neu-knob rounded-full transition-transform ${showHeatmap ? 'translate-x-3' : ''}`} /></span>
                      </button>
                      {/* Escala do calor. O gradiente antigo não tinha escala
                          nenhuma — a mancha não dizia quanto era "muito". */}
                      {showHeatmap && (
                        <div className="flex items-center gap-1.5 -mx-1 px-1 pb-0.5 text-[10px] text-muted-foreground">
                          <span>menos</span>
                          <span className="h-1.5 flex-1 rounded-full" style={{ background: 'linear-gradient(to right,#3b82f6,#22c55e,#facc15,#f97316,#ef4444)' }} />
                          <span>mais</span>
                        </div>
                      )}
                      <button type="button" onClick={() => setShowEmpresarios(p => !p)} className="w-full flex items-center gap-2 -mx-1 px-1 py-0.5 rounded hover:bg-muted/50 text-left">
                        <span className="w-2.5 h-2.5 rounded-full bg-[#f59e0b] flex-shrink-0" />
                        <span className="text-foreground flex-1 truncate">Empresário (C1)</span>
                        <span className="text-muted-foreground">{empresariosAtivas.length}</span>
                        <span className={`relative w-7 h-4 rounded-full neu-switch flex-shrink-0 transition-colors ${showEmpresarios ? 'bg-[#f59e0b]' : 'bg-muted'}`}><span className={`absolute top-0.5 left-0.5 w-3 h-3 bg-white neu-knob rounded-full transition-transform ${showEmpresarios ? 'translate-x-3' : ''}`} /></span>
                      </button>
                      <button type="button" onClick={() => setShowLideres(p => !p)} className="w-full flex items-center gap-2 -mx-1 px-1 py-0.5 rounded hover:bg-muted/50 text-left">
                        <span className="w-2.5 h-2.5 rounded-full bg-[#a855f7] flex-shrink-0" />
                        <span className="text-foreground flex-1 truncate">Líder (C2)</span>
                        <span className="text-muted-foreground">{lideresAtivas.length}</span>
                        <span className={`relative w-7 h-4 rounded-full neu-switch flex-shrink-0 transition-colors ${showLideres ? 'bg-[#a855f7]' : 'bg-muted'}`}><span className={`absolute top-0.5 left-0.5 w-3 h-3 bg-white neu-knob rounded-full transition-transform ${showLideres ? 'translate-x-3' : ''}`} /></span>
                      </button>
                      <button type="button" onClick={() => setShowConsultoras(p => !p)} className="w-full flex items-center gap-2 -mx-1 px-1 py-0.5 rounded hover:bg-muted/50 text-left">
                        <span className="w-2.5 h-2.5 rounded-full bg-[#0ea5e9] flex-shrink-0" />
                        <span className="text-foreground flex-1 truncate">Consultora (C3)</span>
                        <span className="text-muted-foreground">{consultorasAtivas.length}</span>
                        <span className={`relative w-7 h-4 rounded-full neu-switch flex-shrink-0 transition-colors ${showConsultoras ? 'bg-[#0ea5e9]' : 'bg-muted'}`}><span className={`absolute top-0.5 left-0.5 w-3 h-3 bg-white neu-knob rounded-full transition-transform ${showConsultoras ? 'translate-x-3' : ''}`} /></span>
                      </button>
                      <button type="button" onClick={() => setShowInativos(p => !p)} className="w-full flex items-center gap-2 -mx-1 px-1 py-0.5 rounded hover:bg-muted/50 text-left">
                        <span className="text-foreground flex-1 truncate">Inativos</span>
                        {clientesInativosOverlay.length > 0 && <span className="text-muted-foreground">{clientesInativosOverlay.length.toLocaleString()}</span>}
                        <span className={`relative w-7 h-4 rounded-full neu-switch flex-shrink-0 transition-colors ${showInativos ? 'bg-[#9ca3af]' : 'bg-muted'}`}><span className={`absolute top-0.5 left-0.5 w-3 h-3 bg-white neu-knob rounded-full transition-transform ${showInativos ? 'translate-x-3' : ''}`} /></span>
                      </button>
                      {/* "Só localização imprecisa" saiu daqui pra Administração
                          (2026-07-14): é ferramenta de conserto de geo, não camada
                          de leitura — não faz sentido na lista de camadas do dia a dia. */}
                      <button type="button" onClick={() => setShowOrgLinks(p => !p)} className="w-full flex items-center gap-2 -mx-1 px-1 py-0.5 rounded hover:bg-muted/50 text-left" title="Liga cada consultora ativa à líder que responde pelo grupo dela de verdade (não geografia)">
                        <FiUsers className="w-3 h-3 text-violet-500 flex-shrink-0" />
                        <span className="text-foreground flex-1 truncate">Organização (líder real)</span>
                        {showOrgLinks && <span className="text-muted-foreground">{orgLinks.length}</span>}
                        <span className={`relative w-7 h-4 rounded-full neu-switch flex-shrink-0 transition-colors ${showOrgLinks ? 'bg-[#8b5cf6]' : 'bg-muted'}`}><span className={`absolute top-0.5 left-0.5 w-3 h-3 bg-white neu-knob rounded-full transition-transform ${showOrgLinks ? 'translate-x-3' : ''}`} /></span>
                      </button>
                      <button type="button" onClick={() => { setShowMunicipioCoverage(p => !p); setCoberturaLigadaPeloUsuario(true) }} className="w-full flex items-center gap-2 -mx-1 px-1 py-0.5 rounded hover:bg-muted/50 text-left" title="Município verde = tem líder ativa; cinza = sem líder, área a desbloquear">
                        <span className="w-2.5 h-2.5 rounded-sm bg-emerald-500/60 border border-emerald-500 flex-shrink-0" />
                        <span className="text-foreground flex-1 truncate">Cobertura por município</span>
                        <span className={`relative w-7 h-4 rounded-full neu-switch flex-shrink-0 transition-colors ${showMunicipioCoverage ? 'bg-emerald-500' : 'bg-muted'}`}><span className={`absolute top-0.5 left-0.5 w-3 h-3 bg-white neu-knob rounded-full transition-transform ${showMunicipioCoverage ? 'translate-x-3' : ''}`} /></span>
                      </button>
                      {/* Não é camada, é chave de leitura: o pin tracejado com
                          miolo branco marca quem está numa posição FABRICADA
                          (centro do município + ruído de ±3,3 km). Sem isso,
                          chute e endereço exato têm exatamente o mesmo pin. */}
                      {ativasImprecisas > 0 && (
                        <div className="flex items-center gap-2 -mx-1 px-1 py-0.5" title="Coordenada de nível de município: o pin está no centro da cidade com espalhamento aleatório, não no endereço. Conserto em Administração › Só localização imprecisa.">
                          <span className="w-2.5 h-2.5 flex-shrink-0 rounded-full border border-dashed border-foreground/70" />
                          <span className="flex-1 truncate text-muted-foreground">Posição aproximada</span>
                          <span className="text-muted-foreground">{ativasImprecisas}</span>
                        </div>
                      )}
                    </div>

                    {/* Administração — colapsável, minimalista (pedido do dono:
                        a versão anterior tinha informação redundante com o
                        resumo/legenda já visíveis sempre). Só o essencial:
                        status de sync + ação, avisos que exigem atenção,
                        upload de clientes, gestão de semanas, limpar tudo. */}
                    <div className="border-t border-border pt-1.5">
                      <button onClick={() => setShowAdminSection(v => !v)} className="w-full flex items-center justify-between text-muted-foreground hover:text-foreground transition-colors">
                        <span className="font-medium flex items-center gap-1"><FiSettings className="w-3 h-3" /> Administração</span>
                        <FiChevronDown className={`w-3 h-3 transition-transform ${showAdminSection ? 'rotate-180' : ''}`} />
                      </button>

                      {showAdminSection && (
                        <div className="mt-1.5 space-y-1.5">
                          {/* Ferramenta de conserto de geo — filtra só ativas com
                              localização imprecisa pra corrigir arrastando o pino. */}
                          {podeAjustarPin && (
                            <button type="button" onClick={() => setModoAjustePin(p => !p)} className="w-full flex items-center gap-2 px-0.5 py-0.5 rounded hover:bg-muted/50 text-left" title="Enquanto ligado, os pinos podem ser arrastados para corrigir a coordenada. Desligado, o mapa é só leitura — evita mover uma ativa sem querer ao arrastar a tela.">
                              <FiMove className="w-3 h-3 text-amber-500 flex-shrink-0" />
                              <span className="text-foreground flex-1 truncate">Mover pino (corrigir local)</span>
                              <span className={`relative w-7 h-4 rounded-full neu-switch flex-shrink-0 transition-colors ${modoAjustePin ? 'bg-[#f59e0b]' : 'bg-muted'}`}><span className={`absolute top-0.5 left-0.5 w-3 h-3 bg-white neu-knob rounded-full transition-transform ${modoAjustePin ? 'translate-x-3' : ''}`} /></span>
                            </button>
                          )}
                          <button type="button" onClick={() => setOnlyLowConfidence(p => !p)} className="w-full flex items-center gap-2 px-0.5 py-0.5 rounded hover:bg-muted/50 text-left" title="Mostrar só ativas com localização imprecisa (corrigir arrastando o pino)">
                            <FiFilter className="w-3 h-3 text-amber-500 flex-shrink-0" />
                            <span className="text-foreground flex-1 truncate">Só localização imprecisa</span>
                            <span className={`relative w-7 h-4 rounded-full neu-switch flex-shrink-0 transition-colors ${onlyLowConfidence ? 'bg-[#f59e0b]' : 'bg-muted'}`}><span className={`absolute top-0.5 left-0.5 w-3 h-3 bg-white neu-knob rounded-full transition-transform ${onlyLowConfidence ? 'translate-x-3' : ''}`} /></span>
                          </button>
                          {/* Sync Oracle — uma linha */}
                          <div className="flex items-center justify-between px-0.5">
                            <span className="flex items-center gap-1.5 text-muted-foreground truncate">
                              <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                                syncStatus?.isSyncing ? 'bg-amber-500 animate-pulse' :
                                syncStatus?.lastResult?.success === false ? 'bg-red-500' :
                                syncStatus?.lastResult?.success ? 'bg-emerald-500' : 'bg-gray-400'
                              }`} />
                              <span className="truncate">
                                {syncStatus?.isSyncing ? 'Sincronizando Oracle…' :
                                 syncStatus?.lastResult?.timestamp ? `Sync: ${formatSyncTimeAgo(syncStatus.lastResult.timestamp)}` :
                                 'Aguardando sync'}
                              </span>
                            </span>
                            <button
                              onClick={handleSyncNow}
                              disabled={syncStatus?.isSyncing}
                              className="p-1 rounded hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors disabled:opacity-40 flex-shrink-0"
                              title="Sincronizar agora"
                            >
                              <FiRefreshCw className={`w-3 h-3 ${syncStatus?.isSyncing ? 'animate-spin' : ''}`} />
                            </button>
                          </div>
                          {syncStatus?.lastResult?.success === false && (
                            <div className="text-destructive px-0.5 truncate" title={syncStatus.lastResult.error}>{syncStatus.lastResult.error}</div>
                          )}

                          {/* Erro geral */}
                          {error && (
                            <div className="flex items-start gap-2 p-2 bg-destructive/10 border border-destructive/30 rounded-lg">
                              <FiAlertCircle className="w-3.5 h-3.5 text-destructive flex-shrink-0 mt-0.5" />
                              <span className="text-destructive">{error}</span>
                            </div>
                          )}

                          {/* Geocodificação — só aparece quando precisa de atenção/ação */}
                          {ativasGeocodingStatus?.isGeocoding && (
                            <div className="p-2 bg-amber-500/10 border border-amber-500/30 rounded-lg space-y-1">
                              <div className="flex items-center justify-between text-amber-600">
                                <span className="flex items-center gap-1.5"><FiRefreshCw className="w-3 h-3 animate-spin" /> Geocodificando… {ativasGeocodingStatus.percentual}%</span>
                                <button
                                  onClick={async () => { try { await fetchApi('/api/mapa/ativas/geocoding-stop', { method: 'POST' }) } catch (e) { console.error(e) } }}
                                  className="text-red-500 hover:text-red-400" title="Cancelar geocodificação"
                                >
                                  <FiX className="w-3 h-3" />
                                </button>
                              </div>
                              <div className="w-full h-1 bg-amber-500/20 rounded-full overflow-hidden">
                                <div className="h-full bg-amber-500 rounded-full transition-all duration-500" style={{ width: `${ativasGeocodingStatus.percentual}%` }} />
                              </div>
                            </div>
                          )}
                          {ativasGeocodingStatus?.hasError && !ativasGeocodingStatus?.isGeocoding && (
                            <div className="flex items-start gap-2 p-2 bg-red-500/10 border border-red-500/30 rounded-lg">
                              <FiAlertCircle className="w-3.5 h-3.5 text-red-600 flex-shrink-0 mt-0.5" />
                              <span className="text-red-600/90">{ativasGeocodingStatus.statusMessage}</span>
                            </div>
                          )}
                          {(ativasSemCoordenadas > 0 || geocodingErrors.length > 0) && !ativasGeocodingStatus?.isGeocoding && (
                            <div className="flex items-center gap-1.5">
                              {ativasSemCoordenadas > 0 && (
                                <button
                                  onClick={handleRetryGeocoding}
                                  className="flex-1 flex items-center justify-center gap-1 px-2 py-1 bg-amber-500/15 hover:bg-amber-500/25 text-amber-600 rounded-lg transition-colors"
                                  title={`Reprocessar ${ativasSemCoordenadas} registros sem coordenadas`}
                                >
                                  <FiRefreshCw className="w-3 h-3" /> {ativasSemCoordenadas} sem coord.
                                </button>
                              )}
                              {geocodingErrors.length > 0 && (
                                <button
                                  onClick={() => setShowErrorsModal(true)}
                                  className="flex-1 flex items-center justify-center gap-1 px-2 py-1 bg-red-500/15 hover:bg-red-500/25 text-red-600 rounded-lg transition-colors"
                                >
                                  <FiAlertTriangle className="w-3 h-3" /> {geocodingErrors.length} erro{geocodingErrors.length === 1 ? '' : 's'}
                                </button>
                              )}
                            </div>
                          )}

                          {/* Base de clientes — upload manual (recurso à parte de
                              ativas, que já vem do sync Oracle) */}
                          <div className="pt-1 border-t border-border/50">
                            {inativosProgress !== null && (
                              <div className="w-full h-1 bg-muted rounded-full overflow-hidden mb-1">
                                <div className="h-full bg-gray-400 rounded-full transition-all duration-300" style={{ width: `${inativosProgress}%` }} />
                              </div>
                            )}
                            <input
                              ref={fileInputRef}
                              type="file"
                              accept=".txt,.csv,.xls,.xlsx"
                              onChange={handleFileSelect}
                              className="hidden"
                              id="clientes-upload"
                            />
                            <label
                              htmlFor="clientes-upload"
                              className={`flex items-center gap-2 px-1 py-1 rounded-lg transition-colors cursor-pointer ${
                                isUploading ? 'text-blue-500 cursor-wait' : 'text-muted-foreground hover:bg-secondary/50 hover:text-foreground'
                              }`}
                            >
                              {isUploading ? (
                                <><FiRefreshCw className="w-3 h-3 animate-spin" /><span>{uploadProgress?.detail || 'Processando...'}</span></>
                              ) : (
                                <><FiUpload className="w-3 h-3" /><span>Atualizar lista de clientes</span></>
                              )}
                            </label>
                          </div>

                          {ativas.length > 0 && semanas.length === 0 && (
                            <button
                              onClick={handleClearAtivas}
                              disabled={isLoading}
                              className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 bg-destructive/10 hover:bg-destructive/20 text-destructive rounded-lg transition-colors disabled:opacity-50"
                            >
                              <FiTrash2 className="w-3.5 h-3.5" />
                              Limpar todas as ativas
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Resultados da busca — caixa irmã, mesmo estilo, só aparece
                      digitando. Zoom só no clique (nunca automático). */}
                  {matchedPessoas.length > 0 && searchTerm.length >= 2 && (
                    <div className="neu-float rounded-lg shadow-lg w-[min(14rem,calc(100vw-1.5rem))] max-h-full overflow-y-auto text-[11px] pointer-events-auto">
                      <div className="sticky top-0 neu-bar-round px-3 py-2 border-b border-border font-medium text-muted-foreground">
                        {matchedPessoas.length} resultado{matchedPessoas.length === 1 ? '' : 's'}
                      </div>
                      {matchedPessoas.slice(0, 20).map((pessoa, index) => {
                        const tipo = getTipoLabel(pessoa.tipo_consultor)
                        return (
                          <button key={pessoa.codigo} onClick={() => navigateToSearchResult(pessoa, index)}
                            className={`w-full px-3 py-2 text-left hover:bg-muted/50 flex items-start gap-2 border-b border-border/40 last:border-0 transition-colors ${index === selectedSearchIndex ? 'bg-primary/15' : ''}`}>
                            <span className="w-2.5 h-2.5 rounded-full flex-shrink-0 mt-1" style={{ backgroundColor: tipo.color }} />
                            <div className="min-w-0 flex-1">
                              <div className="font-medium truncate text-foreground">{pessoa.nome}</div>
                              <div className="text-muted-foreground flex items-center gap-1.5">
                                <span style={{ color: tipo.color }}>{tipo.label}</span>
                                {pessoa.cidade && <span>• {pessoa.cidade}</span>}
                              </div>
                            </div>
                          </button>
                        )
                      })}
                      {matchedPessoas.length > 20 && (
                        <div className="px-3 py-2 text-muted-foreground text-center">+{matchedPessoas.length - 20} — refine a busca</div>
                      )}
                    </div>
                  )}
                  </>)}
                </div>

                {/* Ações de mapa — movidas pro canto inferior-esquerdo (2026-07-14)
                    pra liberar o topo-direito ao Painel Executivo da diretoria. */}
                <div className="absolute bottom-3 left-3 z-[1000] flex items-center gap-2">
                  <button onClick={handleReset} className="p-2 neu-float-2 rounded-2xl text-foreground hover:bg-foreground/5 transition-colors" title="Centralizar mapa">
                    <FiCrosshair className="w-4 h-4" />
                  </button>
                  {/* Entrar/sair da apresentação — mesmo botão, ícone e cor mudam. */}
                  <button onClick={togglePresentationMode}
                    className={`p-2 rounded-2xl transition-colors ${isPresentationMode ? 'bg-red-500 text-white hover:bg-red-600' : 'neu-float-2 text-foreground hover:bg-foreground/5'}`}
                    title={isPresentationMode ? 'Sair do modo apresentação (ESC)' : 'Modo apresentação'}>
                    {isPresentationMode ? <FiMinimize2 className="w-4 h-4" /> : <FiMonitor className="w-4 h-4" />}
                  </button>
                </div>

                {/* Painel Executivo da diretoria — topo-direito. Recolhe pra uma
                    abinha quando o detalhe de um pin abre. Acoplado ao chip de
                    semana: clicou numa semana → mostra ela; "Todas" → a campanha. */}
                <ExecutivoPanel
                  fetchApi={fetchApi}
                  collapsedByDetail={!!ativaDetail}
                  regiaoFiltrada={ufSelecionada}
                  distritosDaRegiao={distritosDaRegiao}
                  semanaCodigo={(() => {
                    const m = semanaSelecionada?.match(/(\d{4})-W(\d+)/)
                    return m ? Number(m[1]) * 100 + Number(m[2]) : null
                  })()}
                />
              </>

            {/* ─── Painel flutuante de detalhes (mesmo padrão visual do painel
                 Rede, LeaderCoverageView.tsx): substitui o Popup do Leaflet nos
                 pins de ativas. Abre no clique do pin/busca; fecha no X, Escape
                 ou clique no fundo do mapa. Vendas por semana e equipe da líder
                 chegam sob demanda de /api/mapa/ativas/:codigo/detalhe. ─── */}
            {ativaDetail && (() => {
              const cfg = TIPO_CONFIG[ativaDetail.tipo_consultor] || TIPO_CONFIG['3']
              const geo = confidenceMeta(ativaDetail.geo_confidence as GeoConfidence, ativaDetail.geo_source)
              const isLider = ativaDetail.tipo_consultor === '2'
              const equipeFallback = isLider && ativaDetail.grupo_codigo ? equipeValorPorGrupo.get(ativaDetail.grupo_codigo) : undefined
              const titulo = ativaDetail.tipo_consultor === '1' ? 'Detalhes do empresário' : isLider ? 'Detalhes da líder' : 'Detalhes da consultora'
              return (
                <div className="absolute top-14 right-3 z-[1100] neu-float rounded-lg w-[min(18rem,calc(100vw-1.5rem))] max-h-[calc(100%-68px)] overflow-y-auto text-[11px]">
                  <div className="sticky top-0 z-10 neu-bar-round flex items-center justify-between px-3 py-2 border-b border-border">
                    <span className="font-semibold text-foreground">{titulo}</span>
                    <button onClick={() => setAtivaDetail(null)} className="text-muted-foreground hover:text-foreground">
                      <FiX className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="p-3 space-y-2">
                    {/* Identificação */}
                    <div className="flex items-center gap-2">
                      {ativaDetail.profile_pic ? (
                        <img src={ativaDetail.profile_pic} alt="" className="w-8 h-8 rounded-full object-cover shrink-0" style={{ border: `2px solid ${cfg.color}` }} />
                      ) : (
                        <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: cfg.color }} />
                      )}
                      <span className="min-w-0 flex-1">
                        <span className="block font-semibold text-foreground truncate">{ativaDetail.nome}</span>
                        <span className="block font-medium" style={{ color: cfg.color }}>{cfg.label} · #{ativaDetail.codigo}</span>
                      </span>
                    </div>

                    {/* Contato / endereço */}
                    <div className="border-t border-border pt-1.5 space-y-1">
                      <div><span className="text-muted-foreground">Telefone: </span>{ativaDetail.telefone || '—'}</div>
                      <div><span className="text-muted-foreground">Cidade: </span>{[ativaDetail.cidade, ativaDetail.bairro].filter(Boolean).join(' — ') || '—'}</div>
                      {ativaDetail.endereco && (
                        <div><span className="text-muted-foreground">Endereço: </span>{ativaDetail.endereco}{ativaDetail.endereco_numero ? `, ${ativaDetail.endereco_numero}` : ''}</div>
                      )}
                      <div><span className="text-muted-foreground">CEP: </span>{ativaDetail.cep || '—'}</div>
                      {ativaDetail.grupo_nome && <div><span className="text-muted-foreground">Grupo: </span>{ativaDetail.grupo_nome}</div>}
                      {ativaDetail.distrito_nome && <div><span className="text-muted-foreground">Distrito: </span>{ativaDetail.distrito_nome}</div>}
                    </div>

                    {/* Ativação — semana vigente + vendas por semana da vitrine */}
                    <div className="border-t border-border pt-1.5 space-y-1">
                      <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Ativação</div>
                      <div><span className="text-muted-foreground">Ativa na semana: </span><span className="font-medium text-amber-600 dark:text-amber-400">{semanaNome(ativaDetail.semana)}</span></div>
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">Vendas na vitrine</span>
                        <span className="font-bold tabular-nums" style={{ color: cfg.color }}>{formatBRL(detalhe?.vendas ? detalhe.vendas.totalVitrine : (ativaDetail.valor_venda_vitrine || 0))}</span>
                      </div>
                      {detalheLoading && (
                        <div className="flex items-center gap-1.5 text-muted-foreground"><FiRefreshCw className="w-3 h-3 animate-spin" /> Buscando vendas por semana…</div>
                      )}
                      {detalhe?.vendas && detalhe.vendas.porSemana.length > 0 && (
                        <div className="space-y-0.5">
                          {detalhe.vendas.porSemana.map(w => (
                            <div key={w.semana} className="flex items-center gap-1.5 rounded px-1 py-0.5 bg-muted/40" title={semanaNome(w.semana)}>
                              <span className="text-foreground font-medium shrink-0">{semanaCurta(w.semana)}</span>
                              <span className="text-muted-foreground flex-1 truncate">{w.pedidos} pedido{w.pedidos === 1 ? '' : 's'}</span>
                              <span className="tabular-nums text-foreground shrink-0">{formatBRL(w.valor)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      {detalhe?.vendas?.ultimoPedido && (
                        <div><span className="text-muted-foreground">Último pedido: </span>{fmtDataHora(detalhe.vendas.ultimoPedido)}</div>
                      )}
                      {!detalheLoading && detalhe && !detalhe.vendas && (
                        <div className="text-muted-foreground">Vendas por semana indisponíveis (Oracle fora do ar).</div>
                      )}
                    </div>

                    {/* Estrutura do distrito — só pra empresária (C1): líderes
                        abaixo dela (com equipe e valor de cada uma) e totais
                        das consultoras do distrito. Clique numa líder navega
                        pro card dela (que lista a equipe completa). */}
                    {ativaDetail.tipo_consultor === '1' && (
                      <div className="border-t border-border pt-1.5 space-y-1">
                        <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
                          Estrutura{ativaDetail.distrito_nome ? ` — ${ativaDetail.distrito_nome}` : ''}
                        </div>
                        {detalheLoading && (
                          <div className="flex items-center gap-1.5 text-muted-foreground"><FiRefreshCw className="w-3 h-3 animate-spin" /> Carregando estrutura…</div>
                        )}
                        {detalhe?.estrutura && (
                          <>
                            <div className="flex items-center justify-between">
                              <span className="text-muted-foreground">{detalhe.estrutura.totais.lideres} líder{detalhe.estrutura.totais.lideres === 1 ? '' : 'es'} ativa{detalhe.estrutura.totais.lideres === 1 ? '' : 's'}</span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-muted-foreground">{detalhe.estrutura.totais.consultoras} consultora{detalhe.estrutura.totais.consultoras === 1 ? '' : 's'} ativa{detalhe.estrutura.totais.consultoras === 1 ? '' : 's'}</span>
                              <span className="font-bold tabular-nums text-emerald-600 dark:text-emerald-400">{formatBRL(detalhe.estrutura.totais.valorConsultoras)}</span>
                            </div>
                            {detalhe.estrutura.lideres.length > 0 && (
                              <div className="space-y-0.5 max-h-56 overflow-y-auto">
                                {detalhe.estrutura.lideres.map(l => (
                                  <button key={l.codigo} onClick={() => focusEquipeMember(l.codigo)}
                                    title={`${l.nome}${l.cidade ? ` — ${l.cidade}` : ''} · vendas dela ${formatBRL(l.valor_venda_vitrine)} + equipe ${formatBRL(l.equipeValor)}`}
                                    className="w-full flex items-center gap-1.5 text-left rounded px-1 py-1 hover:bg-muted/50">
                                    <span className="w-2 h-2 rounded-full shrink-0 bg-[#a855f7]" />
                                    <span className="flex-1 truncate text-foreground">{l.nome}</span>
                                    <span className="text-muted-foreground shrink-0">{l.equipeCount} cons.</span>
                                    <span className="tabular-nums text-foreground shrink-0">{formatBRL(l.valor_venda_vitrine + l.equipeValor)}</span>
                                  </button>
                                ))}
                              </div>
                            )}
                            {detalhe.estrutura.totais.consultorasSemLiderAtiva > 0 && (
                              <div className="text-muted-foreground">{detalhe.estrutura.totais.consultorasSemLiderAtiva} consultora{detalhe.estrutura.totais.consultorasSemLiderAtiva === 1 ? '' : 's'} ativa{detalhe.estrutura.totais.consultorasSemLiderAtiva === 1 ? '' : 's'} sem líder ativa na vitrine</div>
                            )}
                            {detalhe.estrutura.totais.inativas > 0 && (
                              <div className="text-muted-foreground">Inativas no distrito: {detalhe.estrutura.totais.inativas.toLocaleString('pt-BR')}</div>
                            )}
                          </>
                        )}
                      </div>
                    )}

                    {/* Equipe do grupo — pra líder (equipe dela) e pra empresária
                        com grupo próprio (equipe direta): quem ativou (com valor)
                        e quem está inativa (com a última semana em que ativou) */}
                    {(isLider || (detalhe?.equipe && (detalhe.equipe.ativas.length > 0 || detalhe.equipe.totalInativas > 0))) && (
                      <div className="border-t border-border pt-1.5 space-y-1">
                        <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
                          {isLider ? 'Equipe' : 'Equipe direta'}{ativaDetail.grupo_nome ? ` — ${ativaDetail.grupo_nome}` : ''}
                        </div>
                        {detalheLoading && (
                          <div className="flex items-center gap-1.5 text-muted-foreground">
                            <FiRefreshCw className="w-3 h-3 animate-spin" />
                            {equipeFallback ? `${equipeFallback.count} ativa${equipeFallback.count === 1 ? '' : 's'} · ${formatBRL(equipeFallback.valor)}` : 'Carregando equipe…'}
                          </div>
                        )}
                        {detalhe?.equipe && (
                          <>
                            <div className="flex items-center justify-between">
                              <span className="text-muted-foreground">{detalhe.equipe.ativas.length} consultora{detalhe.equipe.ativas.length === 1 ? '' : 's'} ativa{detalhe.equipe.ativas.length === 1 ? '' : 's'}</span>
                              <span className="font-bold tabular-nums text-emerald-600 dark:text-emerald-400">
                                {formatBRL(detalhe.equipe.ativas.reduce((s, c) => s + c.valor_venda_vitrine, 0))}
                              </span>
                            </div>
                            {detalhe.equipe.ativas.length > 0 && (
                              <div className="space-y-0.5 max-h-48 overflow-y-auto">
                                {detalhe.equipe.ativas.map(c => (
                                  <button key={c.codigo} onClick={() => focusEquipeMember(c.codigo)}
                                    title={`${c.nome}${c.cidade ? ` — ${c.cidade}` : ''} · ativa em ${semanaNome(c.semana_ativa)}`}
                                    className="w-full flex items-center gap-1.5 text-left rounded px-1 py-1 hover:bg-muted/50">
                                    <span className="w-2 h-2 rounded-full shrink-0 bg-[#0ea5e9]" />
                                    <span className="flex-1 truncate text-foreground">{c.nome}</span>
                                    <span className="text-muted-foreground shrink-0">{semanaCurta(c.semana_ativa)}</span>
                                    <span className="tabular-nums text-foreground shrink-0">{formatBRL(c.valor_venda_vitrine)}</span>
                                  </button>
                                ))}
                              </div>
                            )}
                            <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider pt-1">
                              Inativas ({detalhe.equipe.totalInativas})
                            </div>
                            {detalhe.equipe.inativas.length === 0 && (
                              <div className="text-muted-foreground">Nenhuma consultora inativa no grupo.</div>
                            )}
                            {detalhe.equipe.inativas.length > 0 && (
                              <div className="space-y-0.5 max-h-48 overflow-y-auto">
                                {detalhe.equipe.inativas.map(c => (
                                  <div key={c.codigo} title={`${c.nome}${c.cidade ? ` — ${c.cidade}` : ''}`}
                                    className="flex items-center gap-1.5 px-1 py-1">
                                    <span className="w-2 h-2 rounded-full shrink-0 bg-gray-400" />
                                    <span className="flex-1 truncate text-muted-foreground">{c.nome}</span>
                                    <span className="text-muted-foreground shrink-0">{c.ultima_semana_ativa ? `últ. ${semanaCurta(c.ultima_semana_ativa)}` : 'sem registro'}</span>
                                  </div>
                                ))}
                                {detalhe.equipe.totalInativas > detalhe.equipe.inativas.length && (
                                  <div className="px-1 text-muted-foreground">+{detalhe.equipe.totalInativas - detalhe.equipe.inativas.length} inativas não exibidas</div>
                                )}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}

                    {/* Localização + ações */}
                    <div className="border-t border-border pt-1.5 space-y-1">
                      <div title={`Fonte: ${ativaDetail.geo_source || 'desconhecida'}`} className={`flex items-center gap-1.5 ${geo.alert ? 'text-amber-600 dark:text-amber-400' : 'text-muted-foreground'}`}>
                        {geo.alert ? <FiAlertTriangle className="w-3 h-3 shrink-0" /> : <FiCheck className="w-3 h-3 shrink-0" />}
                        <span>Localização: <strong>{geo.label}</strong></span>
                      </div>
                      <div className="text-muted-foreground">Arraste o pino no mapa para corrigir a posição.</div>
                      <button onClick={() => handleOpenEditModal(ativaDetail)}
                        className="flex items-center gap-1.5 px-2 py-1 mt-0.5 rounded border border-border text-foreground hover:bg-muted/50 transition-colors">
                        <FiEdit2 className="w-3 h-3" /> Editar cadastro
                      </button>
                    </div>
                  </div>
                </div>
              )
            })()}

            {/* MAPA DE ATIVAS DA SEMANA (Leaflet + CARTO)

                O mapa fica SEMPRE montado, mesmo com zero pins — o empty-state
                abaixo entra como overlay por cima. Antes ele era condicionado à
                contagem de pins, e um recorte vazio (ex.: a semana corrente, que
                nasce com venda só numa UF) DESTRUÍA a instância do Leaflet:
                ao voltar, tiles, cluster de inativos e 240 polígonos remontavam
                do zero e três comandos de câmera disputavam o enquadramento —
                era o "tremendo em loop" relatado em 18/08. */}
            {(
              <MapContainer
                center={cidadeCenter}
                zoom={12}
                style={{ width: '100%', height: '100%' }}
                zoomControl={false}
                key={`leaflet-ativas-${semanaSelecionada || 'all'}-${mapResetKey}`}
              >
                {/* Recalcula o tamanho do mapa dentro do box encaixotado (2026-07-14) */}
                <MapResizeHandler />
                {/* Zoom no canto inferior-direito (topo-esquerdo é da busca) */}
                <ZoomControl position="bottomright" />
                {/* `key={tileUrl}` força o remount na troca de tema — não tirar.
                    `subdomains` saiu junto com o {s}: o basemap próprio é servido
                    pela própria origem, sem sharding de domínio. */}
                <TileLayer
                  key={tileUrl}
                  attribution={BASEMAP_ATTRIB}
                  url={tileUrl}
                  maxZoom={20}
                />

                {/* Auto-ajustar bounds para mostrar todos os markers */}
                <MapBoundsAdjuster
                  markers={[...empresariosAtivas, ...lideresAtivas, ...consultorasAtivas].map(a => ({
                    lat: a.latitude!,
                    lng: a.longitude!
                  }))}
                  resetKey={ufSelecionada ?? 'all'}
                  enabled={preferencesLoaded}
                />

                {/* Navegação da busca — UI vive na barra única (fora do Leaflet);
                    isto só aciona a câmera quando mapFocus muda. */}
                <MapFlyTo target={mapFocus} />
                {/* Densidade de ativas. Peso 1 por pessoa — sem distinção de
                    hierarquia: mapa de calor responde "onde tem gente", e um
                    empresário não é 1,7 consultora. */}
                <HeatLayer show={showHeatmap} points={heatPoints} />

                {/* Overlay de Inativos — viewport-based, CircleMarker leve */}
                <InativosOverlay
                  clientes={clientesInativosOverlay}
                  show={showInativos}
                  onProgress={setInativosProgress}
                />

                {/* Consultoras (C3) - Marcadores (renderizadas primeiro = camada inferior) */}
                {showConsultoras && consultorasAtivas.map((consultora) => {
                  const dim = isOrgIsolating && consultora.grupo_codigo !== orgFocusGrupo
                  return (
                    <AtivaMarker
                      key={`ativa-consultora-${consultora.codigo}-${markerEpoch}`}
                      position={[consultora.latitude!, consultora.longitude!]}
                      icon={isOrgIsolating && !dim ? getFocusIcon(consultorColor) : getColoredIcon(consultorColor, confidenceMeta(consultora.geo_confidence as GeoConfidence, consultora.geo_source).alert)}
                      draggable={pinArrastavel}
                      opacity={dim ? 0.1 : 1}
                      zIndexOffset={dim ? 0 : 500}
                      eventHandlers={{
                        dragend: (e: any) => { const ll = e.target.getLatLng(); handleMarkerDragEnd(consultora, ll.lat, ll.lng) },
                        click: () => setAtivaDetail(prev => prev?.codigo === consultora.codigo ? null : consultora),
                      }}
                    />
                  )
                })}

                {/* Líderes (C2) - Marcadores (camada média). Clique abre o painel
                    de detalhes e (com a camada Organização ligada) isola ela +
                    seus consultores. Toggle lido via ref — ver AtivaMarker. */}
                {showLideres && lideresAtivas.map((lider) => {
                  const dim = isOrgIsolating && lider.grupo_codigo !== orgFocusGrupo
                  return (
                    <AtivaMarker
                      key={`ativa-lider-${lider.codigo}-${markerEpoch}`}
                      position={[lider.latitude!, lider.longitude!]}
                      icon={isOrgIsolating && !dim ? getFocusIcon(leaderColor) : getColoredIcon(leaderColor, confidenceMeta(lider.geo_confidence as GeoConfidence, lider.geo_source).alert)}
                      draggable={pinArrastavel}
                      opacity={dim ? 0.1 : 1}
                      zIndexOffset={dim ? 0 : 1000}
                      eventHandlers={{
                        dragend: (e: any) => { const ll = e.target.getLatLng(); handleMarkerDragEnd(lider, ll.lat, ll.lng) },
                        click: () => {
                          setAtivaDetail(prev => prev?.codigo === lider.codigo ? null : lider)
                          if (showOrgLinksRef.current && lider.grupo_codigo) {
                            setOrgFocusGrupo(prev => prev === lider.grupo_codigo ? null : lider.grupo_codigo!)
                          }
                        },
                      }}
                    />
                  )
                })}

                {/* Empresários (C1) - Marcadores (camada superior = topo) */}
                {showEmpresarios && empresariosAtivas.map((emp) => {
                  const dim = isOrgIsolating
                  return (
                    <AtivaMarker
                      key={`ativa-empresario-${emp.codigo}-${markerEpoch}`}
                      position={[emp.latitude!, emp.longitude!]}
                      icon={getColoredIcon(empresarioColor, confidenceMeta(emp.geo_confidence as GeoConfidence, emp.geo_source).alert)}
                      draggable={pinArrastavel}
                      opacity={dim ? 0.1 : 1}
                      eventHandlers={{
                        dragend: (e: any) => { const ll = e.target.getLatLng(); handleMarkerDragEnd(emp, ll.lat, ll.lng) },
                        click: () => setAtivaDetail(prev => prev?.codigo === emp.codigo ? null : emp),
                      }}
                    />
                  )
                })}

                {/* Organização real — consultora ativa → líder dona do mesmo
                    grupo_codigo (permanente, trazido do painel Rede). Nem
                    toda consultora resolve (líder do grupo pode não estar
                    ativa nessa semana) — não desenha linha nesse caso. Linha
                    animada (network-line-flow, mesma classe da Rede) e
                    esmaecida fora do grupo focado — key muda com o estado de
                    esmaecimento porque Polyline não reage a só opacity/className
                    (ver memória feedback-react-leaflet-props-update-quirk). */}
                {showOrgLinks && orgLinks.map(link => {
                  const dim = isOrgIsolating && link.grupoCode !== orgFocusGrupo
                  const focus = isOrgIsolating && !dim
                  return (
                    <Polyline key={`${link.key}-${dim ? 'dim' : focus ? 'focus' : 'on'}`} positions={[link.from, link.to]}
                      pathOptions={{ color: '#8b5cf6', weight: focus ? 2.5 : 1.5, opacity: dim ? 0.04 : focus ? 0.85 : 0.6 }}
                      className="network-line-flow">
                      <LTooltip sticky>{link.consultoraNome} → líder {link.liderNome}</LTooltip>
                    </Polyline>
                  )
                })}
                <OrgFocusEffect
                  focusPositions={orgFocusPositions}
                  allPositions={[...empresariosAtivas, ...lideresAtivas, ...consultorasAtivas].map(a => [a.latitude!, a.longitude!] as [number, number])}
                  onBackgroundClick={() => { setOrgFocusGrupo(null); setAtivaDetail(null); setSearchTerm('') }}
                />
                <FocusFade active={isOrgIsolating} />

                {/* Cobertura por município (verde=com líder / cinza=sem líder,
                    área a desbloquear) — mesmo dado e contorno do painel Rede. */}
                {showMunicipioCoverage && municipioGeoMatched && municipioGeoMatched.features.length > 0 && (
                  <>
                    <GeoJSON
                      key={`municipio-cov-${ufSelecionada ?? 'all'}-${municipioGeoMatched.features.length}-${isOrgIsolating ? 'dim' : 'on'}`}
                      data={municipioGeoMatched} style={municipioStyle} onEachFeature={municipioOnEachFeature}
                    />
                    <MunicipioCoverageBoundsAdjuster active={showMunicipioCoverage && coberturaLigadaPeloUsuario} geoData={municipioGeoMatched} />
                  </>
                )}
              </MapContainer>
            )}

            {/* Empty-state PRECISO (2026-07-14): distingue "semana sem ativas"
                (filtro exclui todo mundo — caso W25) de "geocodificação pendente"
                (ativas existem nesta semana mas sem coordenada). Antes mostrava
                "793 aguardando geocodificação" mesmo com tudo geocodificado, só
                porque a semana filtrada estava vazia. */}
            {empresariosAtivas.length + lideresAtivas.length + consultorasAtivas.length === 0 && (() => {
              // Overlay por cima do mapa (que segue montado) — ver comentário do
              // MapContainer. `pointer-events-auto` só no cartão, pra não travar
              // o mapa inteiro embaixo.
              const nomeSemana = semanaSelecionada ? (semanas.find(s => s.semana_codigo === semanaSelecionada)?.nome || semanaSelecionada) : null
              // Só tipos plotáveis (1/2/3) — tipo 0 (Ger. Distribuição) não vira marker.
              // Parte do recorte em foco: com um Estado filtrado, "nenhuma ativa"
              // tem que falar do Estado, não da praça.
              const naSemana = ativasNaRegiao.filter(a => (a.tipo_consultor === '1' || a.tipo_consultor === '2' || a.tipo_consultor === '3') && (!semanaSelecionada || a.semana === semanaSelecionada))
              const comCoordNaSemana = naSemana.filter(a => a.latitude != null).length
              const semDados = ativas.length === 0
              const regiaoVazia = !semDados && !!ufSelecionada && ativasNaRegiao.length === 0
              const semanaVazia = !semDados && !regiaoVazia && semanaSelecionada && naSemana.length === 0
              const aguardandoGeo = !semDados && naSemana.length > 0 && comCoordNaSemana === 0
              return (
              <div className="absolute inset-0 z-[900] flex items-center justify-center bg-secondary/80 backdrop-blur-[2px] pointer-events-none">
                <div className="text-center p-8 max-w-sm neu-float rounded-2xl bg-background/95 pointer-events-auto">
                  <FiMapPin className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    {regiaoVazia ? `Nenhuma ativa em ${ufSelecionada}`
                      : semanaVazia ? `Nenhuma ativa em ${nomeSemana}${ufSelecionada ? ` (${ufSelecionada})` : ''}`
                      : aguardandoGeo ? 'Ativas aguardando geocodificação' : 'Nenhuma ativa com coordenadas'}
                  </h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    {semDados
                      ? 'Faça upload de uma planilha de ativas (.xls) para visualizar no mapa.'
                      : regiaoVazia
                      ? `Nenhuma consultora ativa neste Estado. Clique em "Todos" para ver a praça inteira.`
                      : semanaVazia
                      ? `Essa semana não tem nenhuma consultora ativa (as ativas migram para a semana da última venda). Escolha outra semana ou clique em "Todas".`
                      : `${naSemana.length} ativas nesta semana, aguardando geocodificação…`}
                  </p>
                  {regiaoVazia && (
                    <button onClick={() => selecionarUf(null)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-500 text-white text-sm font-medium hover:bg-amber-600 transition-colors">
                      Ver a praça inteira
                    </button>
                  )}
                  {semanaVazia && (
                    <button onClick={() => setSemanaSelecionada(null)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-500 text-white text-sm font-medium hover:bg-amber-600 transition-colors">
                      Ver todas as semanas
                    </button>
                  )}
                  {aguardandoGeo && ativasGeocodingStatus?.isGeocoding && (
                    <div className="flex items-center justify-center gap-2 text-amber-600">
                      <FiRefreshCw className="w-4 h-4 animate-spin" />
                      <span className="text-sm">Geocodificando {ativasGeocodingStatus.percentual}%</span>
                    </div>
                  )}
                </div>
              </div>
              )
            })()}

            </div>
          </div>

        </div>
      </div>


      {/* Confirmação de mover pin (arrastar pra corrigir posição) */}
      {dragConfirm && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center glass-overlay" onClick={cancelDragMove}>
          <div className="glass-panel rounded-2xl w-[92%] max-w-md p-5" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-3">
              <FiNavigation className="w-5 h-5 text-amber-500" />
              <h3 className="font-semibold text-foreground">Corrigir localização</h3>
            </div>
            <p className="text-sm text-muted-foreground mb-2">
              Mover <strong className="text-foreground">{dragConfirm.ativa.nome}</strong> para a nova posição?
            </p>
            <div className="text-xs bg-secondary/40 rounded-lg p-2.5 mb-3 min-h-[2.5rem] flex items-center">
              {dragConfirm.loading ? (
                <span className="flex items-center gap-2 text-muted-foreground"><FiRefreshCw className="w-3.5 h-3.5 animate-spin" /> Resolvendo endereço…</span>
              ) : (
                <span className="text-foreground flex items-center gap-1.5"><FiMapPin className="w-3.5 h-3.5 flex-shrink-0" /> {dragConfirm.address}</span>
              )}
            </div>
            <div className="text-[11px] text-muted-foreground mb-3">
              Coordenadas: {dragConfirm.lat.toFixed(5)}, {dragConfirm.lng.toFixed(5)} — serão salvas como <strong className="text-foreground">ajuste manual</strong> (alta confiança).
            </div>
            <div className="flex items-center justify-end gap-2">
              <button onClick={cancelDragMove} className="px-3 py-1.5 text-sm rounded-lg text-muted-foreground hover:bg-secondary transition-colors">Cancelar</button>
              <button onClick={confirmDragMove} disabled={dragConfirm.loading}
                className="px-3 py-1.5 text-sm rounded-lg bg-primary text-primary-foreground hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center gap-1.5">
                <FiCheck className="w-4 h-4" /> Salvar posição
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Edição de Cadastro */}
      {editingAtiva && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center glass-overlay"
             onClick={() => setEditingAtiva(null)}>
          <div className="glass-panel rounded-xl w-[92%] max-w-lg max-h-[90vh] flex flex-col"
               onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-3 border-b border-border bg-secondary/30 rounded-t-xl">
              <div className="flex items-center gap-3">
                {editingAtiva.profile_pic ? (
                  <img src={editingAtiva.profile_pic}
                    className="w-11 h-11 rounded-full object-cover"
                    style={{ border: `3px solid ${TIPO_CONFIG[editingAtiva.tipo_consultor]?.color || '#0ea5e9'}` }}
                    alt="" />
                ) : (
                  <div className="w-11 h-11 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: (TIPO_CONFIG[editingAtiva.tipo_consultor]?.color || '#0ea5e9') + '20', border: `3px solid ${TIPO_CONFIG[editingAtiva.tipo_consultor]?.color || '#0ea5e9'}` }}>
                    <FiUser className="w-5 h-5" style={{ color: TIPO_CONFIG[editingAtiva.tipo_consultor]?.color || '#0ea5e9' }} />
                  </div>
                )}
                <div>
                  <h3 className="font-semibold text-base text-foreground">Editar Cadastro</h3>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-xs font-medium px-1.5 py-0.5 rounded"
                      style={{ color: TIPO_CONFIG[editingAtiva.tipo_consultor]?.color, backgroundColor: (TIPO_CONFIG[editingAtiva.tipo_consultor]?.color || '#0ea5e9') + '15' }}>
                      {TIPO_CONFIG[editingAtiva.tipo_consultor]?.label}
                    </span>
                    <span className="text-xs text-muted-foreground">#{editingAtiva.codigo}</span>
                  </div>
                </div>
              </div>
              <button onClick={() => setEditingAtiva(null)}
                      className="p-1.5 hover:bg-secondary rounded-lg transition-colors">
                <FiX className="w-5 h-5" />
              </button>
            </div>

            {/* Form */}
            <div className="flex-1 overflow-auto p-5 space-y-3">
              {[
                { key: 'nome', label: 'Nome', type: 'text', Icon: FiUser },
                { key: 'celular', label: 'Celular', type: 'tel', Icon: FiPhone },
                { key: 'email', label: 'E-mail', type: 'email', Icon: FiMail },
              ].map(({ key, label, type, Icon }) => (
                <div key={key}>
                  <label className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground mb-1">
                    <Icon className="w-3 h-3" /> {label}
                  </label>
                  <input
                    type={type}
                    value={editFormData[key] || ''}
                    onChange={e => setEditFormData(prev => ({ ...prev, [key]: e.target.value }))}
                    className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all"
                  />
                </div>
              ))}

              {/* Separador de endereço */}
              <div className="flex items-center gap-2 pt-2">
                <FiMapPin className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Endereço</span>
                <div className="flex-1 h-px bg-border" />
              </div>

              {/* Endereço em grid */}
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-1">
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">CEP</label>
                  <input
                    type="text"
                    value={editFormData.endereco_cep || ''}
                    onChange={e => setEditFormData(prev => ({ ...prev, endereco_cep: e.target.value }))}
                    className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50"
                  />
                </div>
                <div className="col-span-2">
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Cidade</label>
                  <input
                    type="text"
                    value={editFormData.endereco_cidade || ''}
                    onChange={e => setEditFormData(prev => ({ ...prev, endereco_cidade: e.target.value }))}
                    className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50"
                  />
                </div>
              </div>
              <div className="grid grid-cols-4 gap-3">
                <div className="col-span-3">
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Logradouro</label>
                  <input
                    type="text"
                    value={editFormData.endereco_logradouro || ''}
                    onChange={e => setEditFormData(prev => ({ ...prev, endereco_logradouro: e.target.value }))}
                    className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50"
                  />
                </div>
                <div className="col-span-1">
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Nº</label>
                  <input
                    type="text"
                    value={editFormData.endereco_numero || ''}
                    onChange={e => setEditFormData(prev => ({ ...prev, endereco_numero: e.target.value }))}
                    className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">Bairro</label>
                <input
                  type="text"
                  value={editFormData.endereco_bairro || ''}
                  onChange={e => setEditFormData(prev => ({ ...prev, endereco_bairro: e.target.value }))}
                  onKeyDown={e => { if (e.key === 'Enter') handleSaveEdit() }}
                  className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50"
                />
              </div>

              {/* Aviso de mudança de endereço */}
              {(editFormData.endereco_cep !== (editingAtiva.cep || '') ||
                editFormData.endereco_logradouro !== (editingAtiva.endereco || '') ||
                editFormData.endereco_cidade !== (editingAtiva.cidade || '') ||
                editFormData.endereco_bairro !== (editingAtiva.bairro || '')) && (
                <div className="flex items-start gap-2 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg text-xs text-amber-600">
                  <FiAlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>Ao alterar o endereço, as coordenadas serão recalculadas e o pin será reposicionado após re-geocodificação.</span>
                </div>
              )}

              {/* Erro ao salvar */}
              {editSaveError && (
                <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-sm text-red-500">
                  <FiAlertCircle className="w-4 h-4 flex-shrink-0" />
                  {editSaveError}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-3 px-5 py-3 border-t border-border bg-secondary/20 rounded-b-xl">
              <button onClick={() => setEditingAtiva(null)}
                      className="px-4 py-2 text-sm border border-border rounded-lg hover:bg-secondary transition-colors text-foreground">
                Cancelar
              </button>
              <button onClick={handleSaveEdit}
                      disabled={isSavingEdit}
                      className="flex items-center gap-2 px-4 py-2 text-sm bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50">
                {isSavingEdit ? (
                  <FiRefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <FiCheck className="w-4 h-4" />
                )}
                {isSavingEdit ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tooltip global */}
      <Tooltip
        id="mapa-tooltip"
        place="top"
        className="!neu-pop !text-foreground !px-3 !py-2 !rounded-lg !text-xs !font-medium !shadow-lg !border !border-border !z-50"
      />

      {/* Modal de erros de geocodificação */}
      {showErrorsModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center glass-overlay">
          <div className="glass-panel rounded-xl w-[90%] max-w-3xl max-h-[80vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
              <div className="flex items-center gap-2">
                <FiAlertTriangle className="w-5 h-5 text-red-500" />
                <h3 className="font-semibold text-lg">Erros de Geocodificação ({geocodingErrors.length})</h3>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    const csv = [
                      'Código,Nome,CEP,Cidade,UF,Erro',
                      ...geocodingErrors.map(e =>
                        `"${e.codigo}","${e.nome}","${e.cep}","${e.cidade || ''}","${e.uf || ''}","${e.erro}"`
                      )
                    ].join('\n')
                    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
                    const url = URL.createObjectURL(blob)
                    const a = document.createElement('a')
                    a.href = url
                    a.download = `erros_geocodificacao_${new Date().toISOString().slice(0,10)}.csv`
                    a.click()
                    URL.revokeObjectURL(url)
                  }}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-primary/10 hover:bg-primary/20 text-primary rounded-lg transition-colors"
                >
                  <FiDownload className="w-3.5 h-3.5" />
                  Exportar CSV
                </button>
                <button
                  onClick={() => setShowErrorsModal(false)}
                  className="p-1.5 hover:bg-secondary rounded-lg transition-colors"
                >
                  <FiX className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Lista de erros */}
            <div className="flex-1 overflow-auto p-4">
              <table className="w-full text-sm">
                <thead className="bg-secondary/50 sticky top-0">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium">Código</th>
                    <th className="text-left px-3 py-2 font-medium">Nome</th>
                    <th className="text-left px-3 py-2 font-medium">CEP</th>
                    <th className="text-left px-3 py-2 font-medium">Local</th>
                    <th className="text-left px-3 py-2 font-medium">Erro</th>
                  </tr>
                </thead>
                <tbody>
                  {geocodingErrors.map((err, idx) => (
                    <tr key={idx} className="border-b border-border/50 hover:bg-secondary/30">
                      <td className="px-3 py-2 font-mono text-xs">{err.codigo}</td>
                      <td className="px-3 py-2">{err.nome}</td>
                      <td className="px-3 py-2 font-mono text-xs">{err.cep}</td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">
                        {err.cidade ? `${err.cidade}/${err.uf}` : '-'}
                      </td>
                      <td className="px-3 py-2 text-xs text-red-500">{err.erro}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Footer */}
            <div className="px-4 py-3 border-t border-border bg-secondary/30 text-xs text-muted-foreground">
              Estes CEPs não puderam ser localizados no mapa. Verifique se os CEPs estão corretos na planilha original.
            </div>
          </div>
        </div>
      )}
      {/* Modal de Mapeamento de Arquivo */}
      {showMappingModal && mappingResult && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center glass-overlay" onClick={() => { setShowMappingModal(false); setPendingFile(null) }}>
          <div className="dark glass-panel rounded-xl max-w-3xl w-full mx-4 max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-700 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-white flex items-center gap-2"><FiDatabase className="w-4 h-4" /> Análise do Arquivo</h3>
                {mappingResult.fileInfo && (
                  <p className="text-sm text-gray-400 mt-1">
                    {mappingResult.fileInfo.name} — {mappingResult.fileInfo.rows?.toLocaleString()} linhas, {mappingResult.fileInfo.columns?.length} colunas
                  </p>
                )}
              </div>
              <button onClick={() => { setShowMappingModal(false); setPendingFile(null) }} className="text-gray-400 hover:text-white">
                <FiX className="w-5 h-5" />
              </button>
            </div>

            {/* Body */}
            <div className="px-6 py-4 overflow-y-auto flex-1 space-y-4">
              {/* Summary */}
              {mappingResult.summary && (
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg px-4 py-3 text-sm text-blue-300">
                  {mappingResult.summary}
                </div>
              )}

              {/* Mapping table */}
              {mappingResult.mapping && mappingResult.mapping.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-gray-400 text-left border-b border-gray-700">
                        <th className="pb-2 pr-3">Coluna do Arquivo</th>
                        <th className="pb-2 pr-3">Campo Detectado</th>
                        <th className="pb-2 pr-3">Confiança</th>
                        <th className="pb-2">Amostras</th>
                      </tr>
                    </thead>
                    <tbody>
                      {mappingResult.mapping.map((m: any, i: number) => (
                        <tr key={i} className={i % 2 === 0 ? 'bg-white/[0.02]' : ''}>
                          <td className="py-2 pr-3 text-white font-mono text-xs">{m.fileColumn}</td>
                          <td className="py-2 pr-3 text-gray-300 font-mono text-xs">{m.dbField || '—'}</td>
                          <td className="py-2 pr-3">
                            <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                              m.confidence === 'high' ? 'bg-green-500/20 text-green-400' :
                              m.confidence === 'medium' ? 'bg-yellow-500/20 text-yellow-400' :
                              'bg-gray-500/20 text-gray-500'
                            }`}>
                              {m.confidence === 'high' ? 'Alta' : m.confidence === 'medium' ? 'Média' : 'Nenhuma'}
                            </span>
                          </td>
                          <td className="py-2 text-gray-500 text-xs truncate max-w-[200px]">
                            {m.sampleValues?.slice(0, 3).join(', ')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Unmapped DB fields */}
              {mappingResult.unmappedDbFields && mappingResult.unmappedDbFields.length > 0 && (
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg px-4 py-3">
                  <p className="text-sm text-yellow-400 font-medium mb-1">Campos do banco não mapeados:</p>
                  <p className="text-xs text-yellow-300/70">{mappingResult.unmappedDbFields.join(', ')}</p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-700 flex justify-end gap-3">
              <button
                onClick={() => { setShowMappingModal(false); setPendingFile(null) }}
                className="px-4 py-2 text-sm text-gray-400 hover:text-white border border-gray-600 rounded-lg hover:bg-gray-700 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirmImport}
                className="px-4 py-2 text-sm text-white bg-blue-600 hover:bg-blue-700 rounded-lg font-medium transition-colors"
              >
                Importar Clientes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default MapaDistribuicao
